import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Sparkles } from 'lucide-react';

// Import components
import RackGrid from './components/RackGrid';
import ServerManagement from './components/ServerManagement';
import FloorManagement from './components/FloorManagement';
import PowerManagementModal from './components/PowerManagementModal';
import UpgradesModal from './components/UpgradesModal';
import StatsBar from './components/StatsBar';
import LocationMap from './components/LocationMap';
import GoalsModal from './components/GoalsModal';
import NotificationSystem from './components/NotificationSystem';
import ChillerManagementModal from './components/ChillerManagementModal';
import DatacenterView from './components/DatacenterView';
import GameHeader from './components/GameHeader';
import FloorSelector from './components/FloorSelector';
import WelcomeMessage from './components/WelcomeMessage';
import Test3DViewToggle from './components/Test3DViewToggle';
import FloorDesigner from './components/FloorDesigner';
import SiteLayoutEditor from './components/SiteLayoutEditor';

// Import custom hooks
import { useRackManagement } from './hooks/useRackManagement';
import { usePowerManagement } from './hooks/usePowerManagement';
import { useRandomEvents } from './hooks/useRandomEvents';
import { useGameLoop } from './hooks/useGameLoop';
import { useGoals } from './hooks/useGoals';
import { useHardwareFailures } from './hooks/useHardwareFailures';
import { useFloorManagement } from './hooks/useFloorManagement';
import { useCoolingManagement } from './hooks/useCoolingManagement';
import { useStaffManagement } from './hooks/useStaffManagement';
import { useCustomerSatisfaction } from './hooks/useCustomerSatisfaction';

// Import types and constants
import {
  PowerSupplier,
  Transformer,
  Powerhouse,
  UPS,
  RackType,
  ServerType,
  Rack,
  Server,
  Floor,
  LocationData,
  PowerGrid,
  PowerGrids,
  Locations,
  Upgrades,
  Stats,
  Goal,
  InfrastructureConstraints,
  DebugInfo,
  StatsAccumulator,
  UpgradeType,
  GameNotification,
  EventLogEntry,
  GridCell,
  StaffMember,
  StaffRole
} from './types';

import {
  RACK_TYPES,
  SERVER_TYPES,
  TOR_SWITCH_TYPES,
  HDA_SWITCH_TYPES,
  CORE_SWITCH_TYPES,
  POWER_SUPPLIERS,
  TRANSFORMER_LEVELS,
  POWERHOUSE_LEVELS,
  UPS_LEVELS,
  UPGRADE_TYPES,
  ALL_GOALS,
  POWER_GRIDS,
  SERVER_UNLOCK_THRESHOLDS,
  CONTRACT_TYPES,
  NAWAS_TIERS,
} from './constants';

import { calculateAggregatePUE } from './utils/powerCalculations';
import { computeFacilitySafety, FacilitySafety } from './utils/facilitySafety';

import { useSaveLoad, tryLoadActiveSlot } from './hooks/useSaveLoad';
import SaveLoadModal from './components/SaveLoadModal';
import StartScreen from './components/StartScreen';
import GuidedTour from './components/GuidedTour';
import { getSetupProgress } from './utils/setupProgress';
import { computeUnlockSignals, UNLOCK_TOOL_LABELS, UnlockSignals } from './utils/unlockSignals';
import { useOverlayOpen } from './hooks/useOverlayOpen';
import StaffModal from './components/StaffModal';
import { useNetworkManagement } from './hooks/useNetworkManagement';
import NetworkManagementModal from './components/NetworkManagementModal';
import { useMmrManagement } from './hooks/useMmrManagement';
import MmrModal from './components/MmrModal';
import EventLogModal from './components/EventLogModal';
import FinancialsModal from './components/FinancialsModal';
import BackboneModal from './components/BackboneModal';
import ContractsModal from './components/ContractsModal';
import LoansModal from './components/LoansModal';
import GreenEnergyModal from './components/GreenEnergyModal';
import FleetOverviewModal from './components/FleetOverviewModal';
import { TRANSIT_PROVIDERS, IXP_EXCHANGES, BACKBONE_FIBER_TYPES, RENEWABLE_TYPES, LOCATION_RENEWABLE_FACTORS, CONTRACT_TEMPLATES, CONTRACT_MARKETPLACE_SIZE, CONTRACT_REFRESH_DAYS, GREEN_CERT_RENEWABLE_FRACTION, GREEN_CERT_REVENUE_BONUS, LOAN_OPTIONS, SERVER_TYPES as _SERVER_TYPES_FOR_DELIVERY } from './constants';
import { BackboneLink, AvailableContract, ActiveContract, Loan, PendingDelivery, DailySnapshot } from './types';

// Constants
// Power grid data with proper typing
/* Moved to constants: const POWER_GRIDS: PowerGrids = {
  'netherlands-grid': {
    id: 'netherlands-grid',
    locationName: 'Netherlands Power Grid',
    totalCapacityGW: 2.5,
    currentLoadGW: 0.8,
    suppliers: [
      {
        id: 'supplier-eco',
        name: 'EcoPower Plus',
        costPerKWh: 0.15,
        reliability: 0.99,
        greenEnergy: 0.8,
        maxOutput: 5000 // 5MW
      },
      {
        id: 'supplier-standard',
        name: 'Standard Energy Corp.',
        costPerKWh: 0.12,
        reliability: 0.995,
        greenEnergy: 0.2,
        maxOutput: 10000 // 10MW
      },
      {
        id: 'supplier-industrial',
        name: 'Industrial Grid Services',
        costPerKWh: 0.10,
        reliability: 0.98,
        greenEnergy: 0.05,
        maxOutput: 20000 // 20MW
      }
    ],
    transformer: {
      id: 'transformer-1',
      level: 1,
      capacityKW: 5000,
      efficiency: 0.95,
      cost: 50000,
      supplierId: 'supplier-standard',
      connected: true
    },
    powerhouse: {
      id: 'powerhouse-1',
      level: 1,
      capacityKW: 10000,
      efficiency: 0.92,
      cost: 100000,
      installed: true
    },
    ups: {
      id: 'ups-1',
      level: 1,
      capacityKWh: 1000,
      maxOutputKW: 2500,
      chargeLevelKWh: 1000,
      efficiency: 0.98,
      backupTimeMinutes: 30,
      charging: false,
      discharging: false,
      cost: 75000,
      installed: true
    },
    availablePowerKW: 15000,
    coolingCapacity: 12000
  }
} as const; */

// Power infrastructure upgrade levels are now imported from './constants'

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

// All type definitions have been moved to types.ts

// ============================================================================
// POWER SYSTEM DATA
// ============================================================================

const DEFAULT_POWER_SUPPLIERS: PowerSupplier[] = [
  {
    id: 'supplier-eco',
    name: 'EcoPower Plus',
    costPerKWh: 0.15,
    reliability: 0.99,
    greenEnergy: 0.8,
    maxOutput: 5000 // 5MW
  },
  {
    id: 'supplier-standard',
    name: 'Standard Energy Corp.',
    costPerKWh: 0.12,
    reliability: 0.995,
    greenEnergy: 0.2,
    maxOutput: 10000 // 10MW
  },
  {
    id: 'supplier-industrial',
    name: 'Industrial Grid Services',
    costPerKWh: 0.10,
    reliability: 0.98,
    greenEnergy: 0.05,
    maxOutput: 20000 // 20MW
  }
];

// ============================================================================
// GAME DATA AND CONFIGURATION
// ============================================================================

// RACK_TYPES is now imported from './constants'

// SERVER_TYPES is now imported from './constants'

// UPGRADE_TYPES is now imported from './constants'

// PHASE 2: Floor Quality System - Enhanced with cooling bonuses
const FLOOR_QUALITY_TYPES = [
  {
    id: 'basic',
    name: 'Basic Floor',
    cost: 0,
    coolingBonus: 1.0,
    description: 'Standard raised floor tiles - Adequate airflow',
    color: 'gray',
    badge: '⚫'
  },
  {
    id: 'premium',
    name: 'Premium Floor',
    cost: 150,
    coolingBonus: 1.15,
    description: 'High-airflow perforated tiles - 15% better cooling efficiency',
    color: 'blue',
    badge: '🔵'
  },
  {
    id: 'enterprise',
    name: 'Enterprise Floor',
    cost: 400,
    coolingBonus: 1.3,
    description: 'Advanced airflow management - 30% better cooling efficiency',
    color: 'purple',
    badge: '🟣'
  }
];

// Goals and achievements system are now imported from constants.ts
// ============================================================================
// Helper function to initialize racks for a location and floor
const initializeLocationRacks = (locationId: string, floorId: string): Rack[] => {
  // Create a basic rack layout based on the location and floor
  const racks: Rack[] = [];
  
  // For now, create a simple 3x3 grid of empty racks
  for (let row = 0; row < 3; row++) {
    for (let col = 0; col < 3; col++) {
      // Generate a unique numeric ID using timestamp and position
      const numericId = Date.now() + (row * 100) + col;
      racks.push({
        id: numericId,
        x: col,
        z: row,
        type: 'standard',  // Default rack type
        servers: [],
        maxCapacity: 48,
        floorQuality: 'basic',
        effectiveCooling: 100,  // Base cooling capacity
        powerDraw: 0,  // Initialize with zero power draw
        coolingNeeded: 0  // Initialize with zero cooling needed
      });
    }
  }
  
  return racks;
};

// Helper function to calculate available power for a location
const calculateLocationAvailablePower = (
  locationData: LocationData | null | undefined,
  allPowerGrids: Record<string, PowerGrid>
): number => {
  if (!locationData || !locationData.powerGridId) {
    return 0;
  }

  const grid = allPowerGrids[locationData.powerGridId];
  if (!grid || !grid.suppliers.length) {
    return 0;
  }

  // Require an explicitly selected supplier; no fallback when none selected
  const selectedId = locationData.selectedPowerSupplierId;
  if (!selectedId) {
    return 0;
  }
  const supplier = grid.suppliers.find(s => s.id === selectedId);
  if (!supplier) {
    return 0;
  }

  // Check if we have a transformer and it's properly configured
  if (!locationData.transformer?.connected || !locationData.transformer?.capacityKW) {
    return 0;
  }

  // Power flow: Supplier -> Transformer -> Powerhouse -> UPS -> Racks
  let power = Math.min(supplier.maxOutput, locationData.transformer.capacityKW);
  power *= locationData.transformer.efficiency;

  // Check powerhouse
  if (!locationData.powerhouse?.installed || !locationData.powerhouse?.capacityKW) {
    // If no powerhouse or it's not properly configured, no power is delivered
    return 0;
  }
  power = Math.min(power, locationData.powerhouse.capacityKW);
  power *= locationData.powerhouse.efficiency;

  // UPS is mandatory: must be installed and have capacity
  if (!locationData.ups?.installed || !locationData.ups?.maxOutputKW) {
    return 0;
  }
  power = Math.min(power, locationData.ups.maxOutputKW);
  power *= locationData.ups.efficiency;

  return power;
};

const App = () => {
  console.log(' DatacenterTycoon component is loading...');

  // Utility function for consistent currency formatting
  const formatCurrency = (amount: number, decimals = 0): string => {
    if (decimals > 0) {
      return `$${amount.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
    }
    return `$${Math.floor(amount).toLocaleString()}`;
  };

  // Load saved game once at startup (before state init)
  const initialSave = React.useMemo(() => tryLoadActiveSlot(), []);

  // Core game state - lazily initialized from saved game if present
  const [locations, setLocations] = useState<Record<string, LocationData>>(initialSave?.locations ?? {});
  const [money, setMoney] = useState<number>(initialSave?.money ?? 500000);
  const [gameSpeed, setGameSpeed] = useState<number>(initialSave?.gameSpeed ?? 1);
  const [isPaused, setIsPaused] = useState<boolean>(false);

  // Start/title screen + guided tour. On a fresh page load we show the title
  // screen (Play to begin). "New Game" reloads via resetGame with a sessionStorage
  // flag so the reloaded page skips the title and auto-starts the tour.
  const autostartTour = React.useMemo(() => sessionStorage.getItem('dc_autostart_tour') === '1', []);
  React.useEffect(() => { if (autostartTour) sessionStorage.removeItem('dc_autostart_tour'); }, [autostartTour]);
  const [showStartScreen, setShowStartScreen] = useState<boolean>(!autostartTour);
  const [tourActive, setTourActive] = useState<boolean>(autostartTour);
  // True while any modal/drawer is open — hides the tour/coach UI so it doesn't
  // poke out beside an open panel.
  const overlayOpen = useOverlayOpen(!showStartScreen);
  // gameTime declared early so downstream hooks (useRackManagement) can access it
  const [gameTime, setGameTime] = useState<number>(initialSave?.gameTime ?? 0);
  const gameTimeRef = useRef<number>(initialSave?.gameTime ?? 0);
  useEffect(() => { gameTimeRef.current = gameTime; }, [gameTime]);

  // State for operational costs
  const [operationalCosts, setOperationalCosts] = useState<{ power: number, cooling: number, maintenance: number, staff: number }>({ power: 0, cooling: 0, maintenance: 0, staff: 0 });
  const [reputation, setReputation] = useState<number>(initialSave?.reputation ?? 70);
  const [currentLocation, setCurrentLocation] = useState<string>(initialSave?.currentLocation ?? 'rotterdam');
  const [currentFloor, setCurrentFloor] = useState<string>(initialSave?.currentFloor ?? 'floor1');
  
  // Revenue sparkline — circular buffer of last 60 sampled revenue values (sampled once per tick)
  const revenueHistoryRef = useRef<number[]>([]);
  const [revenueHistory, setRevenueHistory] = useState<number[]>([]);

  // Notification system
  const [notifications, setNotifications] = useState<GameNotification[]>([]);
  const [eventLog, setEventLog] = useState<EventLogEntry[]>([]);
  const [eventLogUnread, setEventLogUnread] = useState(0);
  const [showEventLog, setShowEventLog] = useState(false);

  // Function to trigger a notification — also appends to persistent event log
  const triggerNotification = useCallback((message: string, type: 'success' | 'warning' | 'info' | 'error' = 'info', duration: number = 5000) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const timestamp = Date.now();
    const newNotification: GameNotification = { id, message, type, duration, timestamp };
    setNotifications(prev => [...prev, newNotification]);
    // Append to persistent log (cap at 200 entries, drop oldest)
    setEventLog(prev => {
      const entry: EventLogEntry = { id, message, type, gameTime: gameTimeRef.current, realTime: timestamp };
      const next = [entry, ...prev];
      return next.length > 200 ? next.slice(0, 200) : next;
    });
    setEventLogUnread(prev => prev + 1);
  }, []);
  
  // Function to remove a notification
  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter((notification: GameNotification) => notification.id !== id));
  }, []);
  
  // Use the floor management hook for upgradeFloorQuality and getFloorCoolingEfficiency functions
  const {
    upgradeFloorQuality,
    getFloorCoolingEfficiency
  }: {
    upgradeFloorQuality: (locationId: string, floorId: string, floorQualityId: string) => boolean,
    getFloorCoolingEfficiency: (floorId: string, locationId: string) => number
  } = useFloorManagement({
    locations,
    setLocations,
    money,
    setMoney,
    triggerNotification
  });
  
  // Stable ref for electrician discount — populated after useStaffManagement is called below.
  const electricianDiscountRef = React.useRef<() => number>(() => 1);
  // Stable ref for delivery queuing — populated after state is declared below.
  const addPendingDeliveryRef = React.useRef<(d: PendingDelivery) => void>(() => {});
  // Stable ref for technician count — populated after staff state is declared below.
  const technicianCountRef = React.useRef<number>(0);

  // Use the power management hook for buyPowerUpgrade and selectPowerSupplier functions
  const {
    buyPowerUpgrade,
    selectPowerSupplier,
    buyGenerator,
    refuelGenerator,
  } = usePowerManagement({
    locations,
    setLocations,
    money,
    setMoney,
    triggerNotification,
    formatCurrency,
    getElectricianDiscount: () => electricianDiscountRef.current(),
  });

  // Cooling management hook
  const {
    buyChiller,
    upgradeChiller,
    installHeatRejection,
    upgradeHeatRejection,
    installCrah,
    upgradeCrah,
    calculateLocationCoolingCapacity,
    updateCoolingCapacities
  } = useCoolingManagement({
    locations,
    setLocations,
    money,
    setMoney,
    triggerNotification,
    powerGrids: POWER_GRIDS
  });

  const {
    buyRack,
    removeRack,
    installServer,
    fillRack,
    removeServer,
    installTORSwitch,
    installMpoPatchPanel,
    upgradeMpoPatchPanel,
    installRedundantTorSwitch,
    installManagementSwitch,
  } = useRackManagement({
    locations,
    setLocations,
    money,
    setMoney,
    triggerNotification,
    currentFloor,
    getFloorCoolingEfficiency,
    gameTime,
    addPendingDelivery: (d: PendingDelivery) => addPendingDeliveryRef.current(d),
    technicianCount: technicianCountRef.current,
  });


  const { installHdaSwitch, installCoreSwitch } = useNetworkManagement({
    locations,
    setLocations,
    money,
    setMoney,
    triggerNotification,
    gameTime,
  });

  // UI state
  const [showNetworkModal, setShowNetworkModal] = useState(false);
  const [showMmrModal, setShowMmrModal] = useState(false);
  const [showFinancials, setShowFinancials] = useState(false);
  const [showFloorModal, setShowFloorModal] = useState(false);
  const [showBackboneModal, setShowBackboneModal] = useState(false);
  const [showContractsModal, setShowContractsModal] = useState(false);
  const [showGreenEnergyModal, setShowGreenEnergyModal] = useState(false);

  // New feature state
  const [backboneLinks, setBackboneLinks] = useState<BackboneLink[]>((initialSave as any)?.backboneLinks ?? []);
  const [availableContracts, setAvailableContracts] = useState<AvailableContract[]>((initialSave as any)?.availableContracts ?? []);
  const [activeContracts, setActiveContracts] = useState<ActiveContract[]>((initialSave as any)?.activeContracts ?? []);
  const [lastContractRefreshDay, setLastContractRefreshDay] = useState<number>((initialSave as any)?.lastContractRefreshDay ?? 0);
  const [loans, setLoans] = useState<Loan[]>((initialSave as any)?.loans ?? []);
  const [pendingDeliveries, setPendingDeliveries] = useState<PendingDelivery[]>((initialSave as any)?.pendingDeliveries ?? []);
  const [showLoansModal, setShowLoansModal] = useState<boolean>(false);
  const [dailySnapshots, setDailySnapshots] = useState<DailySnapshot[]>([]);
  const [showFleetOverview, setShowFleetOverview] = useState<boolean>(false);
  const [bankruptcyDaysNegative, setBankruptcyDaysNegative] = useState<number>(0);
  const [showGameOver, setShowGameOver] = useState<boolean>(false);

  // Game progression state - lazily initialized from saved game if present
  const [upgrades, setUpgrades] = useState<Upgrades>(initialSave?.upgrades ?? {
    serverPerformance: 0,
    powerEfficiency: 0,
    coolingEfficiency: 0,
    businessDevelopment: 0,
    disasterPreparedness: 0,
    networkInfrastructure: 0
  });

  const [staff, setStaff] = useState<StaffMember[]>(initialSave?.staff ?? []);
  // Keep technician count ref in sync — read by useRackManagement above to avoid hoisting issues
  technicianCountRef.current = staff.filter((s: any) => s.role === 'technician').length;
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [asnPurchased, setAsnPurchased] = useState<boolean>((initialSave as any)?.asnPurchased ?? false);
  const [piIpPurchased, setPiIpPurchased] = useState<boolean>((initialSave as any)?.piIpPurchased ?? false);

  // Function to trigger notifications is now defined below with the notification system

  const { buildMmr, purchaseAsn, purchasePiIpSpace, orderTransit, cancelTransit, connectIxp, disconnectIxp } = useMmrManagement({
    locations,
    setLocations,
    money,
    setMoney,
    asnPurchased,
    setAsnPurchased,
    piIpPurchased,
    setPiIpPurchased,
    triggerNotification,
    gameTime,
  });

  const subscribeNawas = useCallback((locationId: string, tierId: string) => {
    const tier = NAWAS_TIERS.find(t => t.id === tierId);
    if (!tier) return;
    const loc = locations[locationId];
    if (!loc?.mmr?.built || !asnPurchased || !piIpPurchased) {
      triggerNotification('NaWas requires MMR, ASN and PI IP space.', 'error');
      return;
    }
    if (money < tier.setupCost) {
      triggerNotification(`Need $${tier.setupCost.toLocaleString()} for NaWas setup fee.`, 'error');
      return;
    }
    setMoney(prev => prev - tier.setupCost);
    setLocations(prev => ({
      ...prev,
      [locationId]: { ...prev[locationId], nawasSubscription: { tierId, subscribedAt: gameTime } },
    }));
    triggerNotification(`NaWas ${tier.name} activated at ${loc.name}. DDoS attacks will be scrubbed via BGP reroute.`, 'success');
  }, [locations, money, asnPurchased, piIpPurchased, gameTime, setMoney, setLocations, triggerNotification]);

  const cancelNawas = useCallback((locationId: string) => {
    const loc = locations[locationId];
    if (!loc?.nawasSubscription) return;
    setLocations(prev => ({
      ...prev,
      [locationId]: { ...prev[locationId], nawasSubscription: undefined },
    }));
    triggerNotification('NaWas subscription cancelled.', 'info');
  }, [locations, setLocations, triggerNotification]);

  // ============================================================
  // BACKBONE / DARK FIBER
  // ============================================================

  const installBackboneLink = useCallback((fromLocation: string, toLocation: string, fiberTypeId: string) => {
    const ft = BACKBONE_FIBER_TYPES.find(f => f.id === fiberTypeId);
    if (!ft) return;
    if (money < ft.costPerLink) {
      triggerNotification(`Need ${formatCurrency(ft.costPerLink)} to install ${ft.name}.`, 'error');
      return;
    }
    const id = `backbone-${Date.now()}`;
    setMoney(prev => prev - ft.costPerLink);
    setBackboneLinks(prev => [...prev, { id, fromLocation, toLocation, fiberTypeId, installedAt: gameTime }]);
    triggerNotification(`${ft.name} installed between ${locations[fromLocation]?.name} and ${locations[toLocation]?.name}!`, 'success');
  }, [money, locations, gameTime, formatCurrency, triggerNotification]);

  const upgradeBackboneLink = useCallback((linkId: string, newFiberTypeId: string, upgradeCost: number) => {
    const ft = BACKBONE_FIBER_TYPES.find(f => f.id === newFiberTypeId);
    if (!ft) return;
    if (money < upgradeCost) {
      triggerNotification(`Need ${formatCurrency(upgradeCost)} to upgrade.`, 'error');
      return;
    }
    setMoney(prev => prev - upgradeCost);
    setBackboneLinks(prev => prev.map(l => l.id === linkId ? { ...l, fiberTypeId: newFiberTypeId } : l));
    triggerNotification(`Backbone upgraded to ${ft.name}!`, 'success');
  }, [money, formatCurrency, triggerNotification]);

  const removeBackboneLink = useCallback((linkId: string) => {
    setBackboneLinks(prev => prev.filter(l => l.id !== linkId));
    triggerNotification('Backbone link removed.', 'info');
  }, [triggerNotification]);

  // ============================================================
  // RENEWABLE ENERGY
  // ============================================================

  const installRenewable = useCallback((locationId: string, sourceType: 'solar' | 'wind', units: number) => {
    const rt = RENEWABLE_TYPES.find(r => r.id === sourceType);
    if (!rt) return;
    const cost = rt.costPerUnit * units;
    if (money < cost) {
      triggerNotification(`Need ${formatCurrency(cost)} for ${units} ${rt.name}(s).`, 'error');
      return;
    }
    setMoney(prev => prev - cost);
    setLocations(prev => {
      const loc = prev[locationId];
      const existing: any[] = loc.renewableInstallations ?? [];
      const sameType = existing.find((r: any) => r.sourceType === sourceType);
      let updated: any[];
      if (sameType) {
        updated = existing.map((r: any) => r.sourceType === sourceType ? { ...r, units: r.units + units } : r);
      } else {
        updated = [...existing, { sourceType, units, installedAt: gameTime }];
      }
      return { ...prev, [locationId]: { ...loc, renewableInstallations: updated } };
    });
    triggerNotification(`Installed ${units}× ${rt.name} at ${locations[locationId]?.name}.`, 'success');
  }, [money, locations, gameTime, formatCurrency, triggerNotification]);

  // ============================================================
  // BATTERY STORAGE
  // ============================================================

  const installBattery = useCallback((locationId: string) => {
    const cost = 300000;
    if (money < cost) {
      triggerNotification(`Need ${formatCurrency(cost)} for battery storage.`, 'error');
      return;
    }
    setMoney(prev => prev - cost);
    setLocations(prev => ({
      ...prev,
      [locationId]: {
        ...prev[locationId],
        batteryStorage: {
          installed: true,
          capacityKWh: 1000,
          chargeLevel: 0,
          chargeRateKW: 500,          // 500 kW charge/discharge rate
          exportFeedInFraction: 0.6,  // earn 60% of spot rate on export
          cost,
          tradingEnabled: false,
          buyBelowRate: 0.08,
          sellAboveRate: 0.16,
          dailyArbitrageRevenue: 0,
        },
      },
    }));
    triggerNotification(`Battery storage installed at ${locations[locationId]?.name}.`, 'success');
  }, [money, locations, formatCurrency, triggerNotification]);

  const toggleBatteryTrading = useCallback((locationId: string, enabled: boolean) => {
    setLocations(prev => {
      const loc = prev[locationId];
      if (!loc.batteryStorage) return prev;
      return { ...prev, [locationId]: { ...loc, batteryStorage: { ...loc.batteryStorage, tradingEnabled: enabled } } };
    });
  }, []);

  // ============================================================
  // LOANS
  // ============================================================

  const takeLoan = useCallback((optionId: string) => {
    const opt = LOAN_OPTIONS.find(o => o.id === optionId);
    if (!opt) return;
    if (loans.some(l => !l.paidOff && l.label === opt.label)) {
      triggerNotification('You already have an active loan of this type.', 'warning');
      return;
    }
    const newLoan: Loan = {
      id: `loan-${Date.now()}`,
      label: opt.label,
      principal: opt.principal,
      annualRate: opt.annualRate,
      termDays: opt.termDays,
      startDay: Math.floor(gameTime / 1440),
      remainingBalance: opt.principal,
      paidOff: false,
    };
    setMoney(prev => prev + opt.principal);
    setLoans(prev => [...prev, newLoan]);
    triggerNotification(`${opt.label} approved — ${formatCurrency(opt.principal)} deposited. Daily interest: ${formatCurrency(opt.principal * opt.annualRate / 365)}.`, 'success');
  }, [loans, gameTime, setMoney, triggerNotification]);

  const repayLoan = useCallback((loanId: string) => {
    const loan = loans.find(l => l.id === loanId);
    if (!loan || loan.paidOff) return;
    if (money < loan.remainingBalance) {
      triggerNotification(`Not enough funds. Need ${formatCurrency(loan.remainingBalance)} to repay.`, 'error');
      return;
    }
    setMoney(prev => prev - loan.remainingBalance);
    setLoans(prev => prev.map(l => l.id === loanId ? { ...l, remainingBalance: 0, paidOff: true } : l));
    triggerNotification(`${loan.label} repaid in full. Interest stopped.`, 'success');
  }, [loans, money, setMoney, triggerNotification]);

  const loanCostPerDay = useMemo(() =>
    loans.filter(l => !l.paidOff).reduce((s, l) => s + l.principal * l.annualRate / 365, 0),
  [loans]);

  // Balloon repayment: when a loan term expires, the outstanding principal is due in full.
  // (Interest ran daily over the term; the principal is collected now.)
  useEffect(() => {
    const absoluteDay = Math.floor(gameTime / 1440);
    const expired = loans.filter(l => !l.paidOff && absoluteDay >= l.startDay + l.termDays);
    if (expired.length > 0) {
      const totalDue = expired.reduce((s, l) => s + l.remainingBalance, 0);
      if (totalDue > 0) setMoney(prev => prev - totalDue);
      setLoans(prev => prev.map(l =>
        expired.find(e => e.id === l.id) ? { ...l, paidOff: true, remainingBalance: 0 } : l
      ));
      expired.forEach(l => triggerNotification(
        `${l.label} term ended — ${formatCurrency(l.remainingBalance)} principal repaid in full.`,
        'warning'
      ));
    }
  }, [gameTime, loans, setMoney, formatCurrency, triggerNotification]);

  // ============================================================
  // FINANCIAL HISTORY — sample once per in-game day (max 30 snapshots)
  // ============================================================
  useEffect(() => {
    if (gameTime % 1440 !== 0 || gameTime === 0) return;
    const absoluteDay = Math.floor(gameTime / 1440);
    // Use the single P&L source of truth so the trend chart matches the HUD + actual accrual
    const snap: DailySnapshot = {
      day: absoluteDay,
      revenue: dailyFinancials.revenue,
      costs: dailyFinancials.costs,
      net: dailyFinancials.net,
    };
    setDailySnapshots(prev => [...prev.slice(-29), snap]);
  }, [gameTime]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================================
  // HARDWARE DELIVERIES
  // ============================================================

  const addPendingDelivery = useCallback((d: PendingDelivery) => {
    setPendingDeliveries(prev => [...prev, d]);
  }, []);
  addPendingDeliveryRef.current = addPendingDelivery;

  // Process arrived deliveries each in-game day
  useEffect(() => {
    const absoluteDay = Math.floor(gameTime / 1440);
    if (gameTime % 1440 !== 0) return; // only run on day boundary
    const arrived = pendingDeliveries.filter(d => d.deliveryAtDay <= absoluteDay);
    if (arrived.length === 0) return;

    arrived.forEach(d => {
      const serverType = _SERVER_TYPES_FOR_DELIVERY.find(s => s.id === d.serverTypeId);
      setLocations(prev => {
        const loc = prev[d.locationId];
        if (!loc) return prev; // location gone
        const floor = loc.floors?.[d.floorId];
        if (!floor) return prev; // floor gone
        const rack = floor.racks.find(r => r.id === d.rackId);
        if (!rack || !rack.type) {
          // Rack removed — refund
          setMoney(m => m + d.cost * 0.8);
          triggerNotification(`Delivery for ${serverType?.name ?? 'server'} cancelled (rack removed). 80% refund issued.`, 'warning');
          return prev;
        }
        const newServer: any = {
          id: `srv-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          type: d.serverTypeId,
          status: 'online',
          powerDraw: serverType?.powerDraw ?? 0,
          coolingNeeded: serverType?.coolingNeeded ?? 0,
          revenue: serverType?.revenue ?? 0,
          customers: serverType?.customers ?? 0,
          failureStatus: 'operational',
          satisfactionScore: 100,
          installedAt: gameTime,
          ageStage: 'new',
          ageEfficiencyMultiplier: 1.0,
          utilizationRate: 0.75,
        };
        triggerNotification(`${serverType?.name ?? 'Server'} delivered and installed in ${loc.name}!`, 'success');
        return {
          ...prev,
          [d.locationId]: {
            ...loc,
            floors: {
              ...loc.floors,
              [d.floorId]: {
                ...floor,
                racks: floor.racks.map(r =>
                  r.id === d.rackId ? { ...r, servers: [...r.servers, newServer] } : r
                ),
              },
            },
          },
        };
      });
    });

    setPendingDeliveries(prev => prev.filter(d => d.deliveryAtDay > absoluteDay));
  }, [gameTime, pendingDeliveries, triggerNotification]);

  // ============================================================
  // CONTRACTS
  // ============================================================

  // Helper: set contractId on a list of server IDs within a location
  const assignServersToContract = useCallback((locationId: string, serverIds: string[], contractKey: string) => {
    setLocations(prev => {
      const loc = prev[locationId];
      if (!loc) return prev;
      return {
        ...prev,
        [locationId]: {
          ...loc,
          floors: Object.fromEntries(
            Object.entries(loc.floors).map(([fid, floor]) => [
              fid,
              {
                ...floor,
                racks: floor.racks.map(rack => ({
                  ...rack,
                  servers: rack.servers.map(server =>
                    serverIds.includes(server.id) ? { ...server, contractId: contractKey } : server
                  ),
                })),
              },
            ])
          ),
        },
      };
    });
  }, []);

  // Helper: clear contractId on a list of server IDs within a location
  const freeContractServers = useCallback((locationId: string, serverIds: string[]) => {
    setLocations(prev => {
      const loc = prev[locationId];
      if (!loc) return prev;
      return {
        ...prev,
        [locationId]: {
          ...loc,
          floors: Object.fromEntries(
            Object.entries(loc.floors).map(([fid, floor]) => [
              fid,
              {
                ...floor,
                racks: floor.racks.map(rack => ({
                  ...rack,
                  servers: rack.servers.map(server =>
                    serverIds.includes(server.id) ? { ...server, contractId: undefined } : server
                  ),
                })),
              },
            ])
          ),
        },
      };
    });
  }, []);

  const acceptContract = useCallback((offer: AvailableContract) => {
    const t = offer.template;
    const loc = locations[offer.locationId];
    if (!loc) return;

    // Business Development negotiates better rates: +5% revenue per server per level
    const bizDevRateMult = 1 + ((upgrades.businessDevelopment || 0) * 0.05);
    const effectiveRatePerServer = Math.round(t.revenuePerServerPerDay * bizDevRateMult);

    // Guard: don't allow accepting a duplicate active contract at the same location
    const alreadyActive = activeContracts.find(c => c.templateId === t.id && c.locationId === offer.locationId && c.active);
    if (alreadyActive) {
      triggerNotification(`Already have an active ${t.clientName} contract at this location.`, 'warning');
      return;
    }

    // Check NaWas requirement
    if (t.requiresNawas && !loc.nawasSubscription) {
      triggerNotification(`${t.clientName} requires NaWas DDoS protection. Subscribe first.`, 'error');
      return;
    }

    // Check green cert requirement
    if (t.requiresGreenCert) {
      const renewableKW = (loc.renewableInstallations ?? []).reduce((s, inst) => {
        const rt = (RENEWABLE_TYPES as any[]).find((r: any) => r.id === inst.sourceType);
        return s + (rt?.capacityKW ?? 0) * inst.units * 0.5; // avg factor
      }, 0);
      const dcDrawKW = Object.values(loc.floors ?? {}).reduce((s, f) =>
        s + (f.racks ?? []).reduce((rs, r) => rs + r.servers.reduce((ss, sv) => ss + sv.powerDraw, 0), 0), 0);
      if (dcDrawKW > 0 && renewableKW / dcDrawKW < 0.5) {
        triggerNotification(`${t.clientName} requires green energy certification (≥50% renewable coverage).`, 'error');
        return;
      }
    }

    // Check redundant TOR requirement
    if (t.requiresRedundantTor) {
      const hasRedundantTor = Object.values(loc.floors ?? {}).some(f =>
        (f.racks ?? []).some(r => !!r.torSwitchRedundant)
      );
      if (!hasRedundantTor) {
        triggerNotification(`${t.clientName} requires MLAG redundant TOR switches. Install one in a rack first.`, 'error');
        return;
      }
    }

    const contractKey = `${t.id}|${offer.locationId}`;
    const startDay = Math.floor(gameTime / 1440) + 1;

    // ── Hyperscale: assign servers from multiple locations ───────────────────
    if (t.multiLocation && (t.requiredLocations ?? 1) > 1) {
      const numLocs = t.requiredLocations!;
      // Find all unlocked locations with ≥1 idle qualifying server, sorted by idle count desc
      const locCandidates = Object.entries(locations)
        .filter(([, l]) => l.unlocked)
        .map(([locId, l]) => {
          const idle: { server: Server; tier: number }[] = [];
          Object.values(l.floors ?? {}).forEach(fl => fl.racks.forEach(r => r.servers.forEach(sv => {
            if (sv.contractId || sv.failureStatus === 'failed') return;
            const st = SERVER_TYPES.find(s => s.id === sv.type);
            if (st && st.unlockLevel >= t.minServerTier) idle.push({ server: sv, tier: st.unlockLevel });
          })));
          return { locId, idle };
        })
        .filter(c => c.idle.length > 0)
        .sort((a, b) => b.idle.length - a.idle.length)
        .slice(0, numLocs);

      if (locCandidates.length < numLocs) {
        triggerNotification(
          `${t.clientName} requires servers in ${numLocs} locations. Only ${locCandidates.length} location(s) have qualifying idle servers.`,
          'error'
        );
        return;
      }

      const locationAssignments: Record<string, string[]> = {};
      const allAssigned: string[] = [];
      const perLoc = Math.ceil(t.requiredServerCount / numLocs);

      locCandidates.forEach(({ locId, idle }) => {
        idle.sort((a, b) => b.tier - a.tier);
        const assigned = idle.slice(0, perLoc).map(x => x.server.id);
        locationAssignments[locId] = assigned;
        allAssigned.push(...assigned);
        assignServersToContract(locId, assigned, contractKey);
      });

      const dailyValue = allAssigned.length * effectiveRatePerServer;
      const newContract: ActiveContract = {
        templateId: t.id,
        locationId: offer.locationId,
        tier: t.tier,
        clientName: t.clientName,
        clientType: t.clientType,
        revenuePerServerPerDay: effectiveRatePerServer,
        requiredServerCount: t.requiredServerCount,
        assignedServerIds: allAssigned,
        locationAssignments,
        penaltyPerBreach: t.penaltyPerBreach,
        uptimeRequired: t.uptimeRequired,
        startDay,
        endDay: startDay + t.durationDays,
        breachCount: 0,
        totalEarned: 0,
        active: true,
        bonusRevenuePerDay: dailyValue,
      };

      setActiveContracts(prev => [...prev, newContract]);
      setAvailableContracts(prev => prev.filter(o => !(o.template.id === t.id && o.locationId === offer.locationId)));
      const locNames = locCandidates.map(c => locations[c.locId]?.name).filter(Boolean).join(', ');
      triggerNotification(
        `Hyperscale contract with ${t.clientName} accepted! ${allAssigned.length} servers across ${locCandidates.length} DCs (${locNames}) — ${formatCurrency(dailyValue)}/day.`,
        'success'
      );
      return;
    }

    // ── Standard single-location assignment ──────────────────────────────────
    const eligibleServers: string[] = [];
    const allServers: { server: Server; tier: number }[] = [];
    Object.values(loc.floors ?? {}).forEach(floor => {
      floor.racks.forEach(rack => {
        rack.servers.forEach(server => {
          if (server.contractId) return;
          if (server.failureStatus === 'failed') return;
          const st = SERVER_TYPES.find(s => s.id === server.type);
          if (!st || st.unlockLevel < t.minServerTier) return;
          allServers.push({ server, tier: st.unlockLevel });
        });
      });
    });
    // Sort best tier first so high-spec servers fill premium contracts
    allServers.sort((a, b) => b.tier - a.tier);
    for (const { server } of allServers) {
      if (eligibleServers.length >= t.requiredServerCount) break;
      eligibleServers.push(server.id);
    }

    const dailyValue = eligibleServers.length * effectiveRatePerServer;
    const newContract: ActiveContract = {
      templateId: t.id,
      locationId: offer.locationId,
      tier: t.tier,
      clientName: t.clientName,
      clientType: t.clientType,
      revenuePerServerPerDay: effectiveRatePerServer,
      requiredServerCount: t.requiredServerCount,
      assignedServerIds: eligibleServers,
      penaltyPerBreach: t.penaltyPerBreach,
      uptimeRequired: t.uptimeRequired,
      startDay,
      endDay: startDay + t.durationDays,
      breachCount: 0,
      totalEarned: 0,
      active: true,
      bonusRevenuePerDay: dailyValue,
    };

    assignServersToContract(offer.locationId, eligibleServers, contractKey);
    setActiveContracts(prev => [...prev, newContract]);
    setAvailableContracts(prev => prev.filter(o => !(o.template.id === t.id && o.locationId === offer.locationId)));

    const assigned = eligibleServers.length;
    const needed = t.requiredServerCount;
    const msg = assigned >= needed
      ? `Contract with ${t.clientName} accepted! ${assigned} servers provisioned — ${formatCurrency(dailyValue)}/day.`
      : `Contract with ${t.clientName} accepted. Only ${assigned}/${needed} servers provisioned — install more to earn full value.`;
    triggerNotification(msg, assigned >= needed ? 'success' : 'warning');
  }, [locations, gameTime, activeContracts, upgrades, formatCurrency, triggerNotification, assignServersToContract]);

  const terminateContract = useCallback((key: string) => {
    const [templateId, locationId] = key.split('|');
    const contract = activeContracts.find(c => c.templateId === templateId && c.locationId === locationId && c.active);
    if (!contract) return;
    // Hyperscale: free servers from each assigned location
    if (contract.locationAssignments) {
      Object.entries(contract.locationAssignments).forEach(([locId, sids]) => freeContractServers(locId, sids));
    } else {
      freeContractServers(locationId, contract.assignedServerIds);
    }
    setActiveContracts(prev => prev.map(c =>
      c.templateId === templateId && c.locationId === locationId && c.active
        ? { ...c, active: false }
        : c
    ));
    triggerNotification('Contract terminated. Servers are now idle.', 'warning');
  }, [activeContracts, freeContractServers, triggerNotification]);

  // Auto-assign newly installed idle servers to active contracts that still need more
  useEffect(() => {
    activeContracts.filter(c => c.active).forEach(contract => {
      if (contract.assignedServerIds.length >= contract.requiredServerCount) return;
      const loc = locations[contract.locationId];
      if (!loc) return;
      const template = CONTRACT_TEMPLATES.find(t => t.id === contract.templateId);
      if (!template) return;
      const contractKey = `${contract.templateId}|${contract.locationId}`;
      const needed = contract.requiredServerCount - contract.assignedServerIds.length;
      const newIds: string[] = [];
      const candidates: { server: Server; tier: number }[] = [];
      Object.values(loc.floors ?? {}).forEach(floor => {
        floor.racks.forEach(rack => {
          rack.servers.forEach(server => {
            if (server.contractId) return;
            if (server.failureStatus === 'failed') return;
            const st = SERVER_TYPES.find(s => s.id === server.type);
            if (!st || st.unlockLevel < template.minServerTier) return;
            candidates.push({ server, tier: st.unlockLevel });
          });
        });
      });
      candidates.sort((a, b) => b.tier - a.tier);
      for (const { server } of candidates) {
        if (newIds.length >= needed) break;
        newIds.push(server.id);
      }
      if (newIds.length === 0) return;
      assignServersToContract(contract.locationId, newIds, contractKey);
      setActiveContracts(prev => prev.map(c =>
        c.templateId === contract.templateId && c.locationId === contract.locationId
          ? { ...c, assignedServerIds: [...c.assignedServerIds, ...newIds], bonusRevenuePerDay: (c.assignedServerIds.length + newIds.length) * c.revenuePerServerPerDay }
          : c
      ));
      triggerNotification(
        `${newIds.length} server(s) auto-assigned to ${contract.clientName} contract.`,
        'info'
      );
    });
  }, [locations]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize game state (skipped when loading from a saved game)
  useEffect(() => {
    if (initialSave) return; // already loaded from localStorage

    // Build 18×8 Full Room template grid and racks (match FloorDesigner.applyFullRoom18x8)
    const buildFullRoom18x8 = (): GridCell[][] => {
      const rows = 8;
      const cols = 18;
      const newGrid: GridCell[][] = Array.from({ length: rows }, () => Array.from({ length: cols }, () => ({ type: 'empty' as const })));
      const setRow = (r: number, type: GridCell['type'], start = 1, end = cols - 1) => {
        for (let c = start; c < end; c++) newGrid[r][c] = { type } as GridCell;
      };
      // Walkways top/bottom
      setRow(0, 'empty', 0, cols);
      setRow(7, 'empty', 1, cols - 1);
      // Core bands
      setRow(1, 'hot-aisle');
      setRow(2, 'rack');
      setRow(3, 'cold-aisle');
      setRow(4, 'empty');
      setRow(5, 'rack');
      setRow(6, 'hot-aisle');
      // Cooling at corners
      newGrid[0][0] = { type: 'cooling' };
      newGrid[0][cols - 1] = { type: 'cooling' };
      newGrid[7][0] = { type: 'cooling' };
      newGrid[7][cols - 1] = { type: 'cooling' };
      return newGrid;
    };

    const buildRacksFromGrid = (design: GridCell[][]): Rack[] => {
      const racks: Rack[] = [];
      let counter = 0;
      design.forEach((row, z) => {
        row.forEach((cell, x) => {
          if (cell.type === 'rack') {
            counter += 1;
            racks.push({
              id: Date.now() + counter,
              type: null,
              x,
              z,
              servers: [],
              maxCapacity: 48,
              floorQuality: 'basic',
              effectiveCooling: 100,
              powerDraw: 0,
              coolingNeeded: 0
            });
          }
        });
      });
      return racks;
    };

    const grid1 = buildFullRoom18x8();
    const racks1 = buildRacksFromGrid(grid1);
    const grid2 = buildFullRoom18x8();
    const racks2 = buildRacksFromGrid(grid2);

    // Build initial Site Layout with two top-left rooms (18×8 each) linked to the floors
    const siteWidth = 128;
    const siteHeight = 96;
    const cells = Array.from({ length: siteHeight }, () => Array.from({ length: siteWidth }, () => ({ type: 'empty' as const })));
    const room1Id = 'room-floor1';
    const room2Id = 'room-floor2';
    const roomYOffset = 2; // leave 2-tile top walkway
    const roomWidth = 8;
    const roomHeight = 18; // vertical rooms
    // Room 1 at (0, roomYOffset)
    for (let y = roomYOffset; y < roomYOffset + roomHeight; y++) {
      for (let x = 0; x < roomWidth; x++) {
        cells[y][x] = { type: 'roomRef', roomId: room1Id } as any;
      }
    }
    // Room 2 offset to the right to avoid overlap
    const room2X = roomWidth + 2; // 2-tile gap between rooms
    for (let y = roomYOffset; y < roomYOffset + roomHeight; y++) {
      for (let x = room2X; x < room2X + roomWidth; x++) {
        cells[y][x] = { type: 'roomRef', roomId: room2Id } as any;
      }
    }

    // Update locations with initial data
    setLocations({
      rotterdam: {
        name: 'Heerlen',
        powerGridId: null,
        transformer: null,
        powerhouse: null,
        ups: null,
        chillers: [],
        availablePowerKW: 0,
        coolingCapacity: 0,
        totalCoolingCapacityKW: 0,
        usedCoolingCapacityKW: 0,
        floors: {
          floor1: {
            id: 'floor1',
            name: 'Floor 1',
            level: 0,
            racks: racks1,
            coolingCapacity: 100,
            usedPower: 0,
            usedCooling: 0,
            floorQualityId: 'basic',
            designGrid: grid1
          },
          floor2: {
            id: 'floor2',
            name: 'Floor 2',
            level: 0,
            racks: racks2,
            coolingCapacity: 150,
            usedPower: 0,
            usedCooling: 0,
            floorQualityId: 'basic',
            designGrid: grid2
          }
        },
        unlocked: true,
        unlockCost: 0,
        usedPower: 0,
        usedCooling: 0,
        businessMultiplier: 1.2,  // Higher multiplier for prime location
        status: {
          type: 'operational',
          message: 'Fully operational'
        },
        // Seeded Site Layout and Room assignments
        siteLayout: { width: 128 as const, height: 96 as const, cells } as any,
        rooms: {
          [room1Id]: {
            id: room1Id,
            name: 'Floor 1',
            type: 'cage',
            color: '#2563eb',
            bounds: { x: 0, y: roomYOffset, width: roomWidth, height: roomHeight },
            floorId: 'floor1'
          },
          [room2Id]: {
            id: room2Id,
            name: 'Floor 2',
            type: 'cage',
            color: '#2563eb',
            bounds: { x: room2X, y: roomYOffset, width: roomWidth, height: roomHeight },
            floorId: 'floor2'
          }
        },
        roomAssignments: {
          [room1Id]: 'floor1',
          [room2Id]: 'floor2'
        }
      },
      suburban: {
        name: 'Rotterdam',
        powerGridId: null,
        transformer: null,
        powerhouse: null,
        ups: null,
        chillers: [],
        availablePowerKW: 0,
        coolingCapacity: 80,
        totalCoolingCapacityKW: 0,
        usedCoolingCapacityKW: 0,
        floors: {},
        unlocked: false,
        unlockCost: 5000,
        usedPower: 0,
        usedCooling: 0,
        businessMultiplier: 0.8,  // Lower business activity in suburbs
        status: {
          type: 'locked',
          message: 'Purchase to unlock'
        }
      },
      industrial: {
        name: 'Frankfurt',
        powerGridId: null,
        transformer: null,
        powerhouse: null,
        ups: null,
        chillers: [],
        availablePowerKW: 0,
        coolingCapacity: 25,
        totalCoolingCapacityKW: 0,
        usedCoolingCapacityKW: 0,
        floors: {},
        unlocked: false,
        unlockCost: 10000,
        usedPower: 0,
        usedCooling: 0,
        businessMultiplier: 1.5,
        status: { type: 'locked', message: 'Purchase to unlock' }
      },
      amsterdam: {
        name: 'Amsterdam',
        powerGridId: null, transformer: null, powerhouse: null, ups: null, chillers: [],
        availablePowerKW: 0, coolingCapacity: 0, totalCoolingCapacityKW: 0, usedCoolingCapacityKW: 0,
        floors: {}, unlocked: false, unlockCost: 25000,
        usedPower: 0, usedCooling: 0,
        businessMultiplier: 1.6,
        status: { type: 'locked', message: 'AMS-IX hub — premium peering location' }
      },
      london: {
        name: 'London',
        powerGridId: null, transformer: null, powerhouse: null, ups: null, chillers: [],
        availablePowerKW: 0, coolingCapacity: 0, totalCoolingCapacityKW: 0, usedCoolingCapacityKW: 0,
        floors: {}, unlocked: false, unlockCost: 50000,
        usedPower: 0, usedCooling: 0,
        businessMultiplier: 1.8,
        status: { type: 'locked', message: 'Canary Wharf financial district' }
      },
      paris: {
        name: 'Paris',
        powerGridId: null, transformer: null, powerhouse: null, ups: null, chillers: [],
        availablePowerKW: 0, coolingCapacity: 0, totalCoolingCapacityKW: 0, usedCoolingCapacityKW: 0,
        floors: {}, unlocked: false, unlockCost: 40000,
        usedPower: 0, usedCooling: 0,
        businessMultiplier: 1.7,
        status: { type: 'locked', message: 'La Défense business district' }
      },
      madrid: {
        name: 'Madrid',
        powerGridId: null, transformer: null, powerhouse: null, ups: null, chillers: [],
        availablePowerKW: 0, coolingCapacity: 0, totalCoolingCapacityKW: 0, usedCoolingCapacityKW: 0,
        floors: {}, unlocked: false, unlockCost: 35000,
        usedPower: 0, usedCooling: 0,
        businessMultiplier: 1.4,
        status: { type: 'locked', message: 'Southern European gateway' }
      },
      milan: {
        name: 'Milan',
        powerGridId: null, transformer: null, powerhouse: null, ups: null, chillers: [],
        availablePowerKW: 0, coolingCapacity: 0, totalCoolingCapacityKW: 0, usedCoolingCapacityKW: 0,
        floors: {}, unlocked: false, unlockCost: 45000,
        usedPower: 0, usedCooling: 0,
        businessMultiplier: 1.5,
        status: { type: 'locked', message: 'Mediterranean hub' }
      },
      stockholm: {
        name: 'Stockholm',
        powerGridId: null, transformer: null, powerhouse: null, ups: null, chillers: [],
        availablePowerKW: 0, coolingCapacity: 0, totalCoolingCapacityKW: 0, usedCoolingCapacityKW: 0,
        floors: {}, unlocked: false, unlockCost: 30000,
        usedPower: 0, usedCooling: 0,
        businessMultiplier: 1.5,
        status: { type: 'locked', message: 'Nordic hub — cold climate cooling bonus' }
      },
      warsaw: {
        name: 'Warsaw',
        powerGridId: null, transformer: null, powerhouse: null, ups: null, chillers: [],
        availablePowerKW: 0, coolingCapacity: 0, totalCoolingCapacityKW: 0, usedCoolingCapacityKW: 0,
        floors: {}, unlocked: false, unlockCost: 20000,
        usedPower: 0, usedCooling: 0,
        businessMultiplier: 1.3,
        status: { type: 'locked', message: 'Eastern European emerging market' }
      },
      bucharest: {
        name: 'Bucharest',
        powerGridId: null, transformer: null, powerhouse: null, ups: null, chillers: [],
        availablePowerKW: 0, coolingCapacity: 0, totalCoolingCapacityKW: 0, usedCoolingCapacityKW: 0,
        floors: {}, unlocked: false, unlockCost: 15000,
        usedPower: 0, usedCooling: 0,
        businessMultiplier: 1.1,
        status: { type: 'locked', message: 'Low-cost Eastern European market' }
      },
      istanbul: {
        name: 'Istanbul',
        powerGridId: null, transformer: null, powerhouse: null, ups: null, chillers: [],
        availablePowerKW: 0, coolingCapacity: 0, totalCoolingCapacityKW: 0, usedCoolingCapacityKW: 0,
        floors: {}, unlocked: false, unlockCost: 60000,
        usedPower: 0, usedCooling: 0,
        businessMultiplier: 1.6,
        status: { type: 'locked', message: 'Asia-Europe gateway' }
      },
      // ── The Americas ──
      losangeles: {
        name: 'Los Angeles',
        powerGridId: null, transformer: null, powerhouse: null, ups: null, chillers: [],
        availablePowerKW: 0, coolingCapacity: 0, totalCoolingCapacityKW: 0, usedCoolingCapacityKW: 0,
        floors: {}, unlocked: false, unlockCost: 80000,
        usedPower: 0, usedCooling: 0,
        businessMultiplier: 1.7,
        status: { type: 'locked', message: 'One Wilshire — trans-Pacific cable gateway' }
      },
      seattle: {
        name: 'Seattle',
        powerGridId: null, transformer: null, powerhouse: null, ups: null, chillers: [],
        availablePowerKW: 0, coolingCapacity: 0, totalCoolingCapacityKW: 0, usedCoolingCapacityKW: 0,
        floors: {}, unlocked: false, unlockCost: 55000,
        usedPower: 0, usedCooling: 0,
        businessMultiplier: 1.5,
        status: { type: 'locked', message: 'Cloud country — cool climate cooling bonus' }
      },
      dallas: {
        name: 'Dallas',
        powerGridId: null, transformer: null, powerhouse: null, ups: null, chillers: [],
        availablePowerKW: 0, coolingCapacity: 0, totalCoolingCapacityKW: 0, usedCoolingCapacityKW: 0,
        floors: {}, unlocked: false, unlockCost: 50000,
        usedPower: 0, usedCooling: 0,
        businessMultiplier: 1.5,
        status: { type: 'locked', message: 'Southern US carrier hotel hub — cheap power' }
      },
      chicago: {
        name: 'Chicago',
        powerGridId: null, transformer: null, powerhouse: null, ups: null, chillers: [],
        availablePowerKW: 0, coolingCapacity: 0, totalCoolingCapacityKW: 0, usedCoolingCapacityKW: 0,
        floors: {}, unlocked: false, unlockCost: 65000,
        usedPower: 0, usedCooling: 0,
        businessMultiplier: 1.6,
        status: { type: 'locked', message: 'Financial trading networks — east-west crossroads' }
      },
      ashburn: {
        name: 'Ashburn',
        powerGridId: null, transformer: null, powerhouse: null, ups: null, chillers: [],
        availablePowerKW: 0, coolingCapacity: 0, totalCoolingCapacityKW: 0, usedCoolingCapacityKW: 0,
        floors: {}, unlocked: false, unlockCost: 100000,
        usedPower: 0, usedCooling: 0,
        businessMultiplier: 1.9,
        status: { type: 'locked', message: 'Data Center Alley — the densest DC market on earth' }
      },
      newark: {
        name: 'Newark',
        powerGridId: null, transformer: null, powerhouse: null, ups: null, chillers: [],
        availablePowerKW: 0, coolingCapacity: 0, totalCoolingCapacityKW: 0, usedCoolingCapacityKW: 0,
        floors: {}, unlocked: false, unlockCost: 70000,
        usedPower: 0, usedCooling: 0,
        businessMultiplier: 1.7,
        status: { type: 'locked', message: 'NY metro gateway — transatlantic cable access' }
      },
      saopaulo: {
        name: 'São Paulo',
        powerGridId: null, transformer: null, powerhouse: null, ups: null, chillers: [],
        availablePowerKW: 0, coolingCapacity: 0, totalCoolingCapacityKW: 0, usedCoolingCapacityKW: 0,
        floors: {}, unlocked: false, unlockCost: 45000,
        usedPower: 0, usedCooling: 0,
        businessMultiplier: 1.4,
        status: { type: 'locked', message: 'Latin America\'s largest market — IX.br hub' }
      },
      santiago: {
        name: 'Santiago',
        powerGridId: null, transformer: null, powerhouse: null, ups: null, chillers: [],
        availablePowerKW: 0, coolingCapacity: 0, totalCoolingCapacityKW: 0, usedCoolingCapacityKW: 0,
        floors: {}, unlocked: false, unlockCost: 30000,
        usedPower: 0, usedCooling: 0,
        businessMultiplier: 1.2,
        status: { type: 'locked', message: 'Pacific cable landing — emerging Andean market' }
      },
      buenosaires: {
        name: 'Buenos Aires',
        powerGridId: null, transformer: null, powerhouse: null, ups: null, chillers: [],
        availablePowerKW: 0, coolingCapacity: 0, totalCoolingCapacityKW: 0, usedCoolingCapacityKW: 0,
        floors: {}, unlocked: false, unlockCost: 35000,
        usedPower: 0, usedCooling: 0,
        businessMultiplier: 1.3,
        status: { type: 'locked', message: 'Southern cone hub — low-cost expansion market' }
      },
      // ── Africa / Middle East / Asia-Pacific ──
      johannesburg: {
        name: 'Johannesburg',
        powerGridId: null, transformer: null, powerhouse: null, ups: null, chillers: [],
        availablePowerKW: 0, coolingCapacity: 0, totalCoolingCapacityKW: 0, usedCoolingCapacityKW: 0,
        floors: {}, unlocked: false, unlockCost: 40000,
        usedPower: 0, usedCooling: 0,
        businessMultiplier: 1.3,
        status: { type: 'locked', message: 'Africa\'s digital gateway — NAPAfrica peering' }
      },
      dubai: {
        name: 'Dubai',
        powerGridId: null, transformer: null, powerhouse: null, ups: null, chillers: [],
        availablePowerKW: 0, coolingCapacity: 0, totalCoolingCapacityKW: 0, usedCoolingCapacityKW: 0,
        floors: {}, unlocked: false, unlockCost: 90000,
        usedPower: 0, usedCooling: 0,
        businessMultiplier: 1.8,
        status: { type: 'locked', message: 'Middle East hub — premium market, brutal cooling' }
      },
      hongkong: {
        name: 'Hong Kong',
        powerGridId: null, transformer: null, powerhouse: null, ups: null, chillers: [],
        availablePowerKW: 0, coolingCapacity: 0, totalCoolingCapacityKW: 0, usedCoolingCapacityKW: 0,
        floors: {}, unlocked: false, unlockCost: 85000,
        usedPower: 0, usedCooling: 0,
        businessMultiplier: 1.8,
        status: { type: 'locked', message: 'Asia\'s financial gateway — HKIX peering' }
      },
      tokyo: {
        name: 'Tokyo',
        powerGridId: null, transformer: null, powerhouse: null, ups: null, chillers: [],
        availablePowerKW: 0, coolingCapacity: 0, totalCoolingCapacityKW: 0, usedCoolingCapacityKW: 0,
        floors: {}, unlocked: false, unlockCost: 95000,
        usedPower: 0, usedCooling: 0,
        businessMultiplier: 1.9,
        status: { type: 'locked', message: 'APAC\'s premium market — highest rates in Asia' }
      },
      sydney: {
        name: 'Sydney',
        powerGridId: null, transformer: null, powerhouse: null, ups: null, chillers: [],
        availablePowerKW: 0, coolingCapacity: 0, totalCoolingCapacityKW: 0, usedCoolingCapacityKW: 0,
        floors: {}, unlocked: false, unlockCost: 60000,
        usedPower: 0, usedCooling: 0,
        businessMultiplier: 1.6,
        status: { type: 'locked', message: 'Oceania gateway — trans-Pacific routes' }
      },
    });
  }, []);
  // This section has been moved to the top of the component

  // UI state
  const [selectedRack, setSelectedRack] = useState<number | null>(null);
  const [selectedFloorUpgrade, setSelectedFloorUpgrade] = useState<string>('basic');
  const [showUpgrades, setShowUpgrades] = useState<boolean>(false);
  const [showGoals, setShowGoals] = useState<boolean>(false);
  const [showPowerManagementModal, setShowPowerManagementModal] = useState<boolean>(false);
  const [showChillerManagementModal, setShowChillerManagementModal] = useState<boolean>(false);
  const [showFloorDesigner, setShowFloorDesigner] = useState<boolean>(false);
  const [designerEditFloorId, setDesignerEditFloorId] = useState<string | null>(null);
  const [showSiteLayoutEditor, setShowSiteLayoutEditor] = useState<boolean>(false);
  const [designerInitialTemplate, setDesignerInitialTemplate] = useState<'full' | 'half' | null>(null);
  const [designerAssignRoomId, setDesignerAssignRoomId] = useState<string | null>(null);

  // Notification system is defined at the top of the component
  // MEMOIZED: Current location and floor data
  const currentLocationData = useMemo<LocationData>(() => {
    const location = locations[currentLocation];
    if (!location) {
      // This should not happen with a valid currentLocation, but as a safeguard.
      // We return a minimal, non-functional LocationData object to prevent a crash.
      return {
        name: 'Error - Not Found',
        businessMultiplier: 0,
        powerGridId: null,
        transformer: null,
        powerhouse: null,
        ups: null,
        chillers: [],
        totalCoolingCapacityKW: 0,
        usedCoolingCapacityKW: 0,
        availablePowerKW: 0,
        usedPower: 0,
        coolingCapacity: 0,
        usedCooling: 0,
        floors: {},
        unlocked: false,
        unlockCost: 0,
        status: {
          type: 'error',
          message: 'Location not found'
        }
      };
    }

    const availablePower = calculateLocationAvailablePower(location, POWER_GRIDS);

    return {
      ...location,
      availablePowerKW: availablePower,
    };
  }, [locations, currentLocation]);

  const currentFloorData = useMemo<Floor | null>(() =>
    currentLocationData?.floors ? currentLocationData.floors[currentFloor] : null,
    [currentLocationData, currentFloor]
  );

  const currentRacks = useMemo(() => {
    return currentFloorData?.racks ?? [];
  }, [currentFloorData]);
  
  // Selected rack data
  const selectedRackObj = useMemo(() => {
    if (selectedRack === null || !currentFloorData) return null;
    return currentFloorData.racks.find((rack: Rack) => rack.id === selectedRack) || null;
  }, [selectedRack, currentFloorData]);
  
  const selectedRackType = useMemo(() => {
    if (!selectedRackObj || !selectedRackObj.type) return null;
    return RACK_TYPES.find(rackType => rackType.id === selectedRackObj.type) || null;
  }, [selectedRackObj]);
  
  const serverCount = useMemo(() => {
    return selectedRackObj?.servers?.length || 0;
  }, [selectedRackObj]);
  
  const selectedRackUsedPower = useMemo(() => {
    if (!selectedRackObj) return 0;
    const rackPower = selectedRackObj.powerDraw || 0;
    const serversPower = selectedRackObj.servers?.reduce((total, server) => total + (server.powerDraw || 0), 0) || 0;
    return rackPower + serversPower;
  }, [selectedRackObj]);

  // Compute power warning for StatsBar when connected but missing required components
  const { showPowerWarning, powerWarningMessage } = useMemo(() => {
    const res: { showPowerWarning: boolean; powerWarningMessage: string } = { showPowerWarning: false, powerWarningMessage: '' };
    const loc = currentLocationData as any;
    if (!loc || !loc.powerGridId) return res;
    const missing: string[] = [];
    const hasSupplier = !!loc.selectedPowerSupplierId;
    const transformerOk = !!(loc.transformer?.connected && loc.transformer?.capacityKW);
    const powerhouseOk = !!(loc.powerhouse?.installed && loc.powerhouse?.capacityKW);
    const upsOk = !!(loc.ups?.installed && loc.ups?.maxOutputKW);
    if (!hasSupplier) missing.push('Supplier');
    if (!transformerOk) missing.push('Transformer');
    if (!powerhouseOk) missing.push('Powerhouse');
    if (!upsOk) missing.push('UPS');
    if (missing.length > 0) {
      return { showPowerWarning: true, powerWarningMessage: `Missing: ${missing.join(', ')}` };
    }
    return res;
  }, [currentLocationData]);

  // Game progression state (gameTime declared early above)
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Visual enhancements
  const [showEarningsAnimation, setShowEarningsAnimation] = useState<boolean>(false);
  const [recentEarnings, setRecentEarnings] = useState<number>(0);

  // Phase 4: Infrastructure Systems - Advanced constraints
  const [infrastructureConstraints, setInfrastructureConstraints] = useState<InfrastructureConstraints>({
    powerPanel: { capacity: 1000, position: { x: 0, y: 0 }, installed: true },
    networkMDF: { capacity: 48, position: { x: 0, y: 1 }, installed: true },
    circuits: [],
    cableRuns: []
  });

  // Phase 3: Strategic Planning - Design Mode System
  const [designMode, setDesignMode] = useState<boolean>(false);

  // Phase 4: Infrastructure Systems welcome message
  const [showFloorWelcome, setShowFloorWelcome] = useState<boolean>(true);

  // Goals & Milestones System - now handled by useGoals hook

  // Refs to capture current state for game loop
  const currentStateRef = useRef<any>({});

  // Debug info for tracking game state
  const [debugInfo, setDebugInfo] = useState<DebugInfo>({
    lastRevenueCheck: 0 as number,
    serversFound: 0 as number,
    totalRevenue: 0 as number,
    reputationUsed: 0 as number,
    revenueCalculationAttempts: 0 as number,
    lastGameTick: 0 as number,
    lastMoneyUpdate: 0 as number,
    lastEarningsAmount: 0 as number,
    previousMoney: 0 as number,
    calculatedNewMoney: 0 as number
  });

  // Initialize rack grid for a location
  const initializeLocationRacks = useCallback((locationKey: string, floorKey: string | null = null): Rack[] => {
    const rackGrid: Rack[] = [];
    let gridCols = 16;
    let gridRows = 2;
    let rackIdCounter = 0;

    for (let z = 0; z < gridRows; z++) {
      for (let x = 0; x < gridCols; x++) {
        // Use a numeric ID for Rack.id to match the interface definition
        rackIdCounter++;
        rackGrid.push({
          id: rackIdCounter,
          x,
          z,
          servers: [],
          type: null,
          maxCapacity: 0,
          effectiveCooling: 0,
          floorQuality: 'basic',
          powerDraw: 0,
          coolingNeeded: 0,
        });
      }
    }
    return rackGrid;
  }, []);

  // Initialize locations on first load with enhanced floor quality support
  useEffect(() => {
    setLocations((prev: Locations) => {
      const updated = { ...prev };
      Object.keys(updated).forEach(locationKey => {
        const location = updated[locationKey];

        // Handle Rotterdam with floors
        if (locationKey === 'rotterdam' && location.floors) {
          Object.keys(location.floors).forEach(floorKey => {
            if (location.floors && location.floors[floorKey].racks.length === 0 && !location.floors[floorKey].designGrid) {
              location.floors[floorKey].racks = initializeLocationRacks(locationKey, floorKey);
            } else if (location.floors) {
              // PHASE 2: Ensure existing racks have floor quality (backward compatibility)
              location.floors[floorKey].racks = location.floors[floorKey].racks.map((rack: Rack) => ({
                ...rack,
                floorQuality: rack.floorQuality || 'basic',
                effectiveCooling: rack.effectiveCooling || (rack.type ? RACK_TYPES.find(r => r.id === rack.type)?.cooling || 0 : 0)
              }));
            }
          });
        }
        // Handle other locations without floors
        else if (!location.floors) {
          // Create a default floor with initialized racks
          location.floors = {
            floor1: {
              name: 'Floor 1',
              level: 0,
              coolingCapacity: 0,
              usedPower: 0,
              usedCooling: 0,
              racks: [],
              designGrid: Array.from({ length: 8 }, () => Array.from({ length: 18 }, () => ({ type: 'empty' } as GridCell))) as GridCell[][],
              floorQualityId: 'basic'
            }
          };
        }
      });
      return updated;
    });
  }, [initializeLocationRacks]);

  // Update refs whenever state changes
  useEffect(() => {
    currentStateRef.current = {
      locations,
      upgrades,
      reputation,
    };
  }, [locations, upgrades, reputation]);

  // Staff management
  const {
    hireStaff,
    fireStaff,
    getStaffCount,
    getDailyStaffCost,
    getNetworkEngineerBonus,
    getSecurityReduction,
    getElectricianDiscount,
  } = useStaffManagement({
    staff,
    setStaff,
    money,
    setMoney,
    gameTime,
    triggerNotification,
  });
  // Wire electrician discount into the ref so usePowerManagement picks it up
  electricianDiscountRef.current = getElectricianDiscount;

  // Per-location facility safety derived from equipment placed in the Site Layout Editor.
  // Feeds event probabilities (useRandomEvents) and hardware failure/repair (useHardwareFailures).
  const facilitySafetyByLocation = useMemo<Record<string, FacilitySafety>>(() => {
    const result: Record<string, FacilitySafety> = {};
    Object.entries(locations).forEach(([locId, loc]) => {
      if (!loc.unlocked) return;
      result[locId] = computeFacilitySafety(loc);
    });
    return result;
  }, [locations]);

  // Customer satisfaction & server aging
  const { updateSatisfactionAndAging } = useCustomerSatisfaction({
    locations,
    setLocations,
    isPaused,
    triggerNotification,
  });

  // Use the random events hook — must be before stats so activeEvents is available for DDoS connectivity override
  const {
    activeEvents,
    setActiveEvents,
    checkForRandomEvents,
    processActiveEvents,
    clearEvent,
    tickUtilization,
  } = useRandomEvents({
    locations,
    setLocations,
    gameTime,
    isPaused,
    upgrades,
    securityReduction: getSecurityReduction(),
    facilitySafetyByLocation,
    setReputation,
    triggerNotification
  });

  // Holds each active contract's actual (multiplier-adjusted) daily earnings, keyed by `templateId|locationId`.
  // Populated during the stats memo and read by the daily contract accrual effect.
  const contractEarningsRef = useRef<Record<string, number>>({});

  // MEMOIZED: Calculate total stats across all locations
  const stats: Stats = useMemo(() => {
    let totalRevenue = 0;
    let totalCustomers = 0;
    let totalServers = 0;
    let potentialCustomers = 0;
    let coolingCapacity = 0;

    // Helper: connectivity multiplier for a location (0–1)
    const getConnectivity = (location: LocationData, locKey: string): number => {
      if (!location.mmr?.built) return 0;
      const transitGbps = (location.mmr.transitProviders ?? []).reduce((s, tp) => s + tp.committedGbps, 0);
      const ixpGbps = (location.mmr.ixpConnections ?? []).reduce((s, c) => s + c.portSpeedGbps, 0);
      const externalGbps = transitGbps + ixpGbps;
      if (externalGbps === 0) return 0;
      let serverTrafficGbps = 0;
      Object.values(location.floors ?? {}).forEach(f => {
        f.racks?.forEach(r => {
          r.servers?.forEach(s => {
            if (!s.contractId) return; // idle servers generate no traffic
            const st = SERVER_TYPES.find(x => x.id === s.type);
            if (st) serverTrafficGbps += st.trafficGbps * (s.utilizationRate ?? 1.0);
          });
        });
      });
      if (serverTrafficGbps === 0) return 1;
      const base = Math.min(externalGbps / serverTrafficGbps, 1);
      const underAttack = activeEvents.some(e => e.id === 'ddosAttack' && e.locationId === locKey);
      if (underAttack) return location.nawasSubscription ? base * 0.85 : base * 0.1;
      const networkOutage = activeEvents.some(e => e.id === 'networkOutage' && e.locationId === locKey);
      if (networkOutage) return 0;
      const fiberCut = activeEvents.some(e => e.id === 'fiberCut' && e.locationId === locKey);
      if (fiberCut) return base * 0.3;
      return base;
    };

    // Helper: find a server by ID within a location
    const findServer = (location: LocationData, serverId: string): Server | undefined => {
      for (const floor of Object.values(location.floors ?? {})) {
        for (const rack of floor.racks ?? []) {
          const s = rack.servers?.find(sv => sv.id === serverId);
          if (s) return s;
        }
      }
      return undefined;
    };

    // Per-location: server counts, cooling, potential customers
    Object.entries(locations).forEach(([, location]) => {
      if (!location.unlocked) return;
      Object.values(location.floors ?? {}).forEach(floor => {
        floor.racks?.forEach(rack => { totalServers += rack.servers?.length ?? 0; });
        if (floor.coolingCapacity) coolingCapacity += floor.coolingCapacity;
      });
      const businessDevMultiplier = 1 + ((upgrades.businessDevelopment || 0) * 0.2);
      potentialCustomers += 500 * businessDevMultiplier;
    });

    // Contract revenue — the only source of hosting revenue
    // Servers without a contract earn $0.
    const perfBonus = 1 + (upgrades.serverPerformance * 0.15);
    const networkEngBonus = getNetworkEngineerBonus();
    // Capture each contract's actual (multiplier-adjusted) daily earnings for the totalEarned tally.
    const contractEarnings: Record<string, number> = {};
    activeContracts.filter(c => c.active).forEach(contract => {
      // Hyperscale contracts span multiple locations; revenue is computed per assigned location.
      const contractKey = `${contract.templateId}|${contract.locationId}`;
      const assignmentsByLoc = contract.locationAssignments ?? { [contract.locationId]: contract.assignedServerIds };
      Object.entries(assignmentsByLoc).forEach(([locId, sids]) => {
        const location = locations[locId];
        if (!location?.unlocked) return;
        const connectivity = getConnectivity(location, locId);
        sids.forEach(sid => {
          const server = findServer(location, sid);
          if (!server || server.failureStatus === 'failed') return;
          const satisfactionMult = (server.satisfactionScore ?? 100) / 100;
          const agingMult = server.ageEfficiencyMultiplier ?? 1.0;
          const earned = contract.revenuePerServerPerDay
            * (location.businessMultiplier ?? 1)
            * connectivity
            * (reputation / 100)
            * perfBonus
            * networkEngBonus
            * satisfactionMult
            * agingMult;
          totalRevenue += earned;
          contractEarnings[contractKey] = (contractEarnings[contractKey] ?? 0) + earned;
          totalCustomers++;
        });
      });
    });
    contractEarningsRef.current = contractEarnings;

    // Network Infrastructure upgrade: +20% customer capacity per level
    const networkCapacityMult = 1 + ((upgrades.networkInfrastructure || 0) * 0.2);

    return {
      totalRevenue,
      totalCustomers: Math.round(totalCustomers * networkCapacityMult),
      totalServers,
      totalPotentialCustomers: potentialCustomers,
      totalCooling: coolingCapacity,
    };
  }, [locations, upgrades, reputation, activeEvents, activeContracts]);

  // Revenue breakdown — used by StatsBar tooltip
  const revenueBreakdown = useMemo(() => {
    let serversWithRevenue = 0;
    let serversDown = 0;
    let locationsNoMmr = 0;
    let locationsNoTransit = 0;
    let locationsPartial = 0;
    let reputationPct = Math.round(reputation);
    Object.values(locations).forEach(loc => {
      if (!loc.unlocked) return;
      const hasServers = Object.values(loc.floors ?? {}).some(f => (f.racks ?? []).some(r => (r.servers ?? []).length > 0));
      if (!hasServers) return;
      if (!loc.mmr?.built) { locationsNoMmr++; return; }
      const transitGbps = (loc.mmr.transitProviders ?? []).reduce((s, tp) => s + tp.committedGbps, 0);
      const ixpGbps = (loc.mmr.ixpConnections ?? []).reduce((s, c) => s + c.portSpeedGbps, 0);
      const externalGbps = transitGbps + ixpGbps;
      if (externalGbps === 0) { locationsNoTransit++; return; }
      let serverTrafficGbps = 0;
      Object.values(loc.floors ?? {}).forEach(f => f.racks?.forEach(r => r.servers?.forEach(s => {
        const st = SERVER_TYPES.find(x => x.id === s.type);
        if (st) serverTrafficGbps += st.trafficGbps * (s.utilizationRate ?? 1);
      })));
      if (externalGbps < serverTrafficGbps) locationsPartial++;
      Object.values(loc.floors ?? {}).forEach(f => f.racks?.forEach(r => r.servers?.forEach(s => {
        if (s.failureStatus === 'failed' || s.failureStatus === 'repairing') serversDown++;
        else serversWithRevenue++;
      })));
    });
    return { serversWithRevenue, serversDown, locationsNoMmr, locationsNoTransit, locationsPartial, reputationPct };
  }, [locations, reputation]);

  // Player progression level — derived from total servers installed
  const playerLevel = useMemo(() => {
    const total = stats.totalServers;
    const levels = Object.entries(SERVER_UNLOCK_THRESHOLDS)
      .map(([lvl, threshold]) => ({ lvl: Number(lvl), threshold }))
      .sort((a, b) => b.lvl - a.lvl);
    return levels.find(l => total >= l.threshold)?.lvl ?? 0;
  }, [stats.totalServers]);

  // Aggregate PUE across all unlocked locations
  const pue = useMemo(() => calculateAggregatePUE(locations), [locations]);

  // Use the hardware failures hook
  const { checkForFailures, repairServer } = useHardwareFailures({
    locations,
    setLocations,
    money,
    setMoney,
    gameTime,
    isPaused,
    currentFloor,
    upgrades,
    technicianCount: getStaffCount('technician'),
    facilitySafetyByLocation,
    triggerNotification,
  });

  // Use the goals hook
  const {
    goals,
    goalProgress,
    completedGoals,
    claimedGoals,
    checkGoalProgress,
    claimGoal
  } = useGoals({
    stats,
    locations,
    money,
    upgrades,
    staffCount: staff.length,
    staffRoleCount: new Set(staff.map(s => s.role)).size,
    triggerNotification,
    setMoney
  });
  
  // Bandwidth saturation penalty — computed per tick cycle from current locations state.
  // Applied as a daily revenue reduction in useGameLoop.
  // Also returns a per-layer breakdown for the StatsBar tooltip.
  const { bandwidthPenaltyPerDay, bandwidthBreakdown } = useMemo(() => {
    let totalPenalty = 0;
    let torPenalty = 0;
    let hdaPenalty = 0;
    let corePenalty = 0;
    let saturatedTorRacks = 0;
    let saturatedHdaFloors = 0;
    let saturatedCoreLocations = 0;

    Object.values(locations).forEach(location => {
      if (!location.unlocked) return;
      let locationEgressGbps = 0;

      Object.values(location.floors ?? {}).forEach(floor => {
        const hda = floor.hdaSwitch
          ? HDA_SWITCH_TYPES.find(t => t.id === floor.hdaSwitch!.typeId)
          : null;

        let floorEgressGbps = 0;
        (floor.racks ?? []).forEach(rack => {
          if (!rack.type) return;
          if (!rack.torSwitch) return;
          const tor = TOR_SWITCH_TYPES.find(t => t.id === rack.torSwitch!.typeId);
          if (!tor) return;
          const uplinkGbps = tor.uplinkBandwidthGbps * (rack.torSwitchRedundant ? 2 : 1);
          const rackTraffic = (rack.servers ?? []).reduce((sum, s) => {
            if (!s.contractId || s.failureStatus === 'failed') return sum;
            const st = SERVER_TYPES.find(t => t.id === s.type);
            return sum + (st?.trafficGbps ?? 0) * (s.utilizationRate ?? 1.0);
          }, 0);
          if (rackTraffic > uplinkGbps && uplinkGbps > 0) {
            const overage = rackTraffic / uplinkGbps;
            const rackRevenue = (rack.servers ?? []).reduce((s, sv) => s + sv.revenue, 0);
            const pen = rackRevenue * Math.min(0.5, (overage - 1) * 0.5);
            totalPenalty += pen;
            torPenalty += pen;
            saturatedTorRacks++;
          }
          floorEgressGbps += Math.min(rackTraffic, uplinkGbps);
        });

        if (hda) {
          if (floorEgressGbps > hda.bandwidthGbps) {
            const overage = floorEgressGbps / hda.bandwidthGbps;
            const floorRevenue = (floor.racks ?? []).reduce((s, r) =>
              s + (r.servers ?? []).reduce((s2, sv) => s2 + sv.revenue, 0), 0);
            const pen = floorRevenue * Math.min(0.5, (overage - 1) * 0.5);
            totalPenalty += pen;
            hdaPenalty += pen;
            saturatedHdaFloors++;
          }
        } else if (floorEgressGbps > 10) {
          const floorRevenue = (floor.racks ?? []).reduce((s, r) =>
            s + (r.servers ?? []).reduce((s2, sv) => s2 + sv.revenue, 0), 0);
          const pen = floorRevenue * 0.15;
          totalPenalty += pen;
          hdaPenalty += pen;
          saturatedHdaFloors++;
        }

        locationEgressGbps += hda
          ? Math.min(floorEgressGbps, hda.bandwidthGbps)
          : floorEgressGbps;
      });

      const coreSwitch = location.coreSwitch
        ? CORE_SWITCH_TYPES.find(t => t.id === location.coreSwitch!.typeId)
        : null;
      if (coreSwitch) {
        if (locationEgressGbps > coreSwitch.bandwidthGbps) {
          const overage = locationEgressGbps / coreSwitch.bandwidthGbps;
          const locationRevenue = Object.values(location.floors ?? {}).reduce((s, fl) =>
            s + (fl.racks ?? []).reduce((s2, r) =>
              s2 + (r.servers ?? []).reduce((s3, sv) => s3 + sv.revenue, 0), 0), 0);
          const pen = locationRevenue * Math.min(0.5, (overage - 1) * 0.5);
          totalPenalty += pen;
          corePenalty += pen;
          saturatedCoreLocations++;
        }
      } else if (locationEgressGbps > 40) {
        const locationRevenue = Object.values(location.floors ?? {}).reduce((s, fl) =>
          s + (fl.racks ?? []).reduce((s2, r) =>
            s2 + (r.servers ?? []).reduce((s3, sv) => s3 + sv.revenue, 0), 0), 0);
        const pen = locationRevenue * 0.20;
        totalPenalty += pen;
        corePenalty += pen;
        saturatedCoreLocations++;
      }
    });

    return {
      bandwidthPenaltyPerDay: totalPenalty,
      bandwidthBreakdown: { torPenalty, hdaPenalty, corePenalty, saturatedTorRacks, saturatedHdaFloors, saturatedCoreLocations },
    };
  }, [locations]);

  // Total cooling used vs capacity across all unlocked locations
  const coolingStats = useMemo(() => {
    let used = 0;
    let capacity = 0;
    Object.values(locations).forEach(loc => {
      if (!loc.unlocked) return;
      used += loc.usedCoolingCapacityKW ?? 0;
      capacity += loc.totalCoolingCapacityKW ?? 0;
    });
    return { used, capacity };
  }, [locations]);

  // Transit + IXP costs — with peering ratio discount applied per location
  const transitCostPerDay = useMemo(() => {
    let total = 0;
    Object.values(locations).forEach(location => {
      if (!location.unlocked || !location.mmr?.built) return;
      const transitProviders = location.mmr.transitProviders ?? [];
      const ixpConnections = location.mmr.ixpConnections ?? [];

      const totalTransitGbps = transitProviders.reduce((s, tp) => s + tp.committedGbps, 0);
      const totalIxpGbps = ixpConnections.reduce((s, c) => s + c.portSpeedGbps, 0);
      const peeringRatioOk = totalTransitGbps > 0 && (totalIxpGbps / (totalIxpGbps + totalTransitGbps)) >= 0.70;
      const transitDiscount = peeringRatioOk ? 0.85 : 1.0;

      transitProviders.forEach(tp => {
        const provider = TRANSIT_PROVIDERS.find(p => p.id === tp.providerId);
        if (provider) total += (provider.costPerGbpsMonth * tp.committedGbps * transitDiscount) / 30;
      });
      ixpConnections.forEach(c => {
        const ixp = IXP_EXCHANGES.find(x => x.id === c.ixpId);
        const port = ixp?.ports.find(p => p.speedGbps === c.portSpeedGbps);
        if (port) total += port.monthlyCost / 30;
      });
      // NaWas monthly subscription cost
      if (location.nawasSubscription) {
        const tier = NAWAS_TIERS.find(t => t.id === location.nawasSubscription!.tierId);
        if (tier) total += tier.monthlyCost / 30;
      }
    });
    return total;
  }, [locations]);

  // Transit capacity overview — provisioned Gbps vs live server traffic demand across all locations
  const transitStats = useMemo(() => {
    let provisionedGbps = 0;
    let demandGbps = 0;       // traffic from locations with MMR (counts against transit)
    let totalTrafficGbps = 0; // all server traffic across every location (for live feed)
    let hasAnyMmr = false;
    Object.values(locations).forEach(location => {
      if (!location.unlocked) return;
      // Sum server traffic for all locations (live feed)
      let locTraffic = 0;
      Object.values(location.floors ?? {}).forEach(f => {
        f.racks?.forEach(r => {
          r.servers?.forEach(s => {
            const st = SERVER_TYPES.find(x => x.id === s.type);
            if (st && s.contractId && s.failureStatus !== 'failed') {
              locTraffic += st.trafficGbps * (s.utilizationRate ?? 1.0);
            }
          });
        });
      });
      totalTrafficGbps += locTraffic;
      if (!location.mmr?.built) return;
      hasAnyMmr = true;
      provisionedGbps += (location.mmr.transitProviders ?? []).reduce((s, tp) => s + tp.committedGbps, 0);
      provisionedGbps += (location.mmr.ixpConnections ?? []).reduce((s, c) => s + c.portSpeedGbps, 0);
      demandGbps += locTraffic;
    });
    return { provisionedGbps, demandGbps, totalTrafficGbps, hasAnyMmr };
  }, [locations]);

  // IXP revenue bonus — per-location bonus applied to server revenue
  const ixpRevenueBonusPerDay = useMemo(() => {
    let total = 0;
    Object.entries(locations).forEach(([locId, location]) => {
      if (!location.unlocked || !location.mmr?.built) return;
      const ixpConnections = location.mmr.ixpConnections ?? [];
      if (ixpConnections.length === 0) return;

      // Cap bonus at 2 IXP connections per location
      const bonusMultiplier = Math.min(ixpConnections.length, 2) === 1
        ? ixpConnections.reduce((max, c) => {
            const ixp = IXP_EXCHANGES.find(x => x.id === c.ixpId);
            return Math.max(max, ixp?.revenueBonus ?? 0);
          }, 0)
        : ixpConnections.reduce((sum, c) => {
            const ixp = IXP_EXCHANGES.find(x => x.id === c.ixpId);
            return sum + (ixp?.revenueBonus ?? 0);
          }, 0);

      // Base revenue for this location = sum of active contract revenues here
      const locationContractRevenue = activeContracts
        .filter(c => c.active && c.locationId === locId)
        .reduce((s, c) => s + c.assignedServerIds.length * c.revenuePerServerPerDay, 0);
      total += locationContractRevenue * Math.min(bonusMultiplier, 0.20);
    });
    return total;
  }, [locations, activeContracts]);

  // ============================================================
  // RENEWABLE / BACKBONE / CONTRACT USEMEMOS
  // ============================================================

  // Average renewable power output per location (kW), accounting for hourly profiles
  const renewablePowerKWByLocation = useMemo(() => {
    const result: Record<string, number> = {};
    Object.entries(locations).forEach(([locId, loc]) => {
      if (!loc.unlocked) return;
      const installations = loc.renewableInstallations ?? [];
      let totalKW = 0;
      const locFactors = LOCATION_RENEWABLE_FACTORS[locId] ?? { solar: 1, wind: 1 };
      installations.forEach(inst => {
        const rt = RENEWABLE_TYPES.find(r => r.id === inst.sourceType);
        if (!rt) return;
        const profile = rt.hourlyProfile;
        const avgFraction = profile.reduce((s, v) => s + v, 0) / profile.length;
        const factor = inst.sourceType === 'solar' ? locFactors.solar : locFactors.wind;
        totalKW += rt.capacityKW * inst.units * avgFraction * factor;
      });
      result[locId] = totalKW;
    });
    return result;
  }, [locations]);

  // Locations with green energy certification (≥50% renewable coverage)
  const greenCertifiedLocations = useMemo(() => {
    return Object.entries(locations)
      .filter(([locId, loc]) => {
        if (!loc.unlocked) return false;
        const renewableKW = renewablePowerKWByLocation[locId] ?? 0;
        if (renewableKW === 0) return false;
        const totalPowerDrawKW = Object.values(loc.floors ?? {}).reduce((acc, floor) =>
          acc + floor.racks.reduce((ra, rack) =>
            ra + rack.servers.reduce((sa, s) => sa + s.powerDraw, 0), 0), 0);
        if (totalPowerDrawKW === 0) return false;
        return renewableKW / totalPowerDrawKW >= GREEN_CERT_RENEWABLE_FRACTION;
      })
      .map(([locId]) => locId);
  }, [locations, renewablePowerKWByLocation]);

  // Renewable savings are now implicit in the game loop — grid draw is reduced by renewable
  // output each tick in calculatePowerCosts. This memo is kept for display purposes only
  // (shows approximate daily saving based on 24h average output vs DC draw).
  const renewableSavingsPerDay = useMemo(() => {
    let total = 0;
    Object.entries(locations).forEach(([locId, loc]) => {
      if (!loc.unlocked || !loc.selectedPowerSupplierId) return;
      const grid = POWER_GRIDS[loc.powerGridId ?? ''];
      const supplier = grid?.suppliers.find((s: any) => s.id === loc.selectedPowerSupplierId);
      if (!supplier) return;
      const avgRenewableKW = renewablePowerKWByLocation[locId] ?? 0;
      const totalPowerDrawKW = Object.values(loc.floors ?? {}).reduce((acc, floor) =>
        acc + floor.racks.reduce((ra, rack) =>
          ra + rack.servers.reduce((sa, s) => sa + s.powerDraw, 0), 0), 0);
      const effectiveSavingKW = Math.min(avgRenewableKW, totalPowerDrawKW);
      total += effectiveSavingKW * supplier.costPerKWh * 24;
    });
    return total;
  }, [locations, renewablePowerKWByLocation]);

  // Green cert revenue bonus per day
  const greenCertRevenueBonusPerDay = useMemo(() => {
    // Apply green cert bonus to actual contract earnings at certified locations
    let total = 0;
    activeContracts.filter(c => c.active && greenCertifiedLocations.includes(c.locationId)).forEach(contract => {
      const loc = locations[contract.locationId];
      if (!loc) return;
      total += contract.revenuePerServerPerDay
        * contract.assignedServerIds.length
        * GREEN_CERT_REVENUE_BONUS
        * (loc.businessMultiplier ?? 1);
    });
    return total;
  }, [locations, greenCertifiedLocations, activeContracts]);

  // Backbone link monthly cost → daily cost
  const backboneCostPerDay = useMemo(() => {
    return backboneLinks.reduce((acc, link) => {
      const ft = BACKBONE_FIBER_TYPES.find(f => f.id === link.fiberTypeId);
      return acc + (ft?.monthlyFeePerLink ?? 0) / 30;
    }, 0);
  }, [backboneLinks]);

  // Per-link traffic utilization: sum of server traffic at both endpoints / link capacity
  const backboneLinkStats = useMemo(() => {
    const result: Record<string, { usedGbps: number; capacityGbps: number; utilization: number }> = {};
    backboneLinks.forEach(link => {
      const ft = BACKBONE_FIBER_TYPES.find(f => f.id === link.fiberTypeId);
      if (!ft) return;
      let totalTraffic = 0;
      [link.fromLocation, link.toLocation].forEach(locId => {
        const loc = locations[locId];
        if (!loc?.unlocked) return;
        Object.values(loc.floors ?? {}).forEach(f => {
          f.racks?.forEach(r => {
            r.servers?.forEach(s => {
              const st = SERVER_TYPES.find(x => x.id === s.type);
              if (st && s.failureStatus !== 'failed') totalTraffic += st.trafficGbps * (s.utilizationRate ?? 1);
            });
          });
        });
      });
      const usedGbps = Math.min(totalTraffic, ft.capacityGbps);
      result[link.id] = { usedGbps, capacityGbps: ft.capacityGbps, utilization: usedGbps / ft.capacityGbps };
    });
    return result;
  }, [backboneLinks, locations]);

  // Backbone transit savings: for each link, backbone traffic reduces transit cost
  // Simplified: backbone capacity shared between both endpoints; reduces transit $/Gbps cost
  const backboneSavingsPerDay = useMemo(() => {
    if (backboneLinks.length === 0) return 0;
    let savings = 0;
    backboneLinks.forEach(link => {
      const ft = BACKBONE_FIBER_TYPES.find(f => f.id === link.fiberTypeId);
      if (!ft) return;
      // For each endpoint, see what transit they're paying and how much backbone offloads
      [link.fromLocation, link.toLocation].forEach(locId => {
        const loc = locations[locId];
        if (!loc?.mmr?.built) return;
        loc.mmr.transitProviders.forEach(tp => {
          const provider = TRANSIT_PROVIDERS.find(p => p.id === tp.providerId);
          if (!provider) return;
          // Backbone offloads up to its capacity in Gbps from this provider
          const offloadGbps = Math.min(ft.capacityGbps / 2, tp.committedGbps);
          const offloadFraction = offloadGbps / Math.max(tp.committedGbps, 1);
          savings += (provider.costPerGbpsMonth * tp.committedGbps * offloadFraction) / 30;
        });
      });
    });
    return savings;
  }, [backboneLinks, locations]);

  // Contract revenue is included in stats.totalRevenue (via activeContracts in that memo).
  // No separate contractBonusPerDay needed — keeping zero for game loop compatibility.
  const contractBonusPerDay = 0;

  // Renewable maintenance cost per day
  const renewableMaintenanceCostPerDay = useMemo(() => {
    let total = 0;
    Object.values(locations).forEach(loc => {
      if (!loc.unlocked) return;
      (loc.renewableInstallations ?? []).forEach(inst => {
        const rt = RENEWABLE_TYPES.find(r => r.id === inst.sourceType);
        if (rt) total += (rt.monthlyMaintenance * inst.units) / 30;
      });
    });
    return total;
  }, [locations]);

  // Display-only event revenue modifier — mirrors processActiveEvents but without mutating duration.
  // Used so StatsBar net/day reflects active event boosts/penalties in real time.
  const eventRevenueModifier = useMemo(() => {
    let total = 0;
    activeEvents.forEach(event => {
      const location = locations[event.locationId];
      if (!location) return;
      const locRev = Object.values(location.floors ?? {}).reduce((s, fl) =>
        s + (fl.racks ?? []).reduce((s2, r) =>
          s2 + (r.servers ?? []).reduce((s3, sv) => s3 + (sv.revenue ?? 0) * (location.businessMultiplier ?? 1), 0), 0), 0);
      const effect = event.effect(location, locRev);
      if (effect.revenueloss) total -= effect.revenueloss;
      if (effect.revenueboost) total += effect.revenueboost;
    });
    return total;
  }, [activeEvents, locations]);

  // Single source of truth for daily P&L — mirrors the dailyNet formula in useGameLoop
  // exactly, so the StatsBar Net/day chip and the financial-history snapshots always
  // agree with the money the game loop actually accrues.
  const dailyFinancials = useMemo(() => {
    const revenue =
      stats.totalRevenue
      + eventRevenueModifier
      + ixpRevenueBonusPerDay
      + contractBonusPerDay
      + backboneSavingsPerDay
      + greenCertRevenueBonusPerDay;
    const costs =
      operationalCosts.power
      + operationalCosts.cooling
      + operationalCosts.maintenance
      + operationalCosts.staff
      + bandwidthPenaltyPerDay
      + transitCostPerDay
      + backboneCostPerDay
      + renewableMaintenanceCostPerDay
      + loanCostPerDay;
    return { revenue, costs, net: revenue - costs };
  }, [
    stats.totalRevenue, eventRevenueModifier, ixpRevenueBonusPerDay, contractBonusPerDay,
    backboneSavingsPerDay, greenCertRevenueBonusPerDay, operationalCosts,
    bandwidthPenaltyPerDay, transitCostPerDay, backboneCostPerDay,
    renewableMaintenanceCostPerDay, loanCostPerDay,
  ]);

  // ── Progressive disclosure: announce newly unlocked Management tools ────────
  // When an unlock signal first flips true, toast the tools it opened up and
  // pulse the Manage button so the player discovers them (they'd otherwise appear
  // silently in a closed drawer). prevUnlockRef seeds from the first render so a
  // loaded save doesn't re-announce tools the player already has.
  const [manageUnlockPulse, setManageUnlockPulse] = useState(false);
  const prevUnlockRef = useRef<UnlockSignals | null>(null);
  useEffect(() => {
    const signals = computeUnlockSignals(locations, activeContracts);
    const prev = prevUnlockRef.current;
    prevUnlockRef.current = signals;
    if (!prev) return; // first run — seed only, never announce on load
    (Object.keys(signals) as (keyof UnlockSignals)[]).forEach(key => {
      if (signals[key] && !prev[key]) {
        const tools = UNLOCK_TOOL_LABELS[key];
        const shown = tools.slice(0, 3).join(', ');
        const more = tools.length > 3 ? ` & ${tools.length - 3} more` : '';
        triggerNotification(`New tools unlocked in Manage: ${shown}${more}.`, 'success', 8000);
        setManageUnlockPulse(true);
      }
    });
  }, [locations, activeContracts, triggerNotification]);

  // Use the game loop hook
  const { intervalRef: gameLoopIntervalRef } = useGameLoop({
    gameTime,
    setGameTime,
    isPaused: isPaused || showStartScreen,
    gameSpeed,
    money,
    setMoney,
    stats,
    setRecentEarnings,
    setShowEarningsAnimation,
    setDebugInfo,
    checkForRandomEvents,
    processActiveEvents,
    checkForFailures,
    updateSatisfactionAndAging,
    tickUtilization,
    activeEvents,
    dailyStaffCost: getDailyStaffCost(),
    bandwidthPenaltyPerDay,
    transitCostPerDay,
    ixpRevenueBonusPerDay,
    locations,
    setLocations,
    powerGrids: POWER_GRIDS,
    setOperationalCosts,
    setReputation,
    contractBonusPerDay,
    renewableMaintenanceCostPerDay,
    backboneCostPerDay,
    backboneSavingsPerDay,
    greenCertRevenueBonusPerDay,
    loanCostPerDay,
    upgrades,
  });
  
  // Update intervalRef with the one from useGameLoop
  useEffect(() => {
    intervalRef.current = gameLoopIntervalRef.current;
  }, [gameLoopIntervalRef]);

  // Save / Load system
  const { saveToSlot, loadFromSlot, deleteSlot } = useSaveLoad({
    money, reputation, gameTime, gameSpeed,
    locations, upgrades, staff,
    currentLocation, currentFloor,
    asnPurchased, piIpPurchased, activeEvents,
    backboneLinks, availableContracts, activeContracts, lastContractRefreshDay,
    loans, pendingDeliveries,
    setMoney, setReputation, setGameTime, setGameSpeed,
    setLocations, setUpgrades, setStaff,
    setCurrentLocation, setCurrentFloor,
    setAsnPurchased, setPiIpPurchased, setActiveEvents,
    setBackboneLinks, setAvailableContracts, setActiveContracts, setLastContractRefreshDay,
    setLoans, setPendingDeliveries,
    triggerNotification,
  });

  const [showSaveModal, setShowSaveModal] = useState(false);

  const resetGame = useCallback(() => {
    // Clear the active-slot pointer and autosave so a page refresh after reset
    // doesn't silently reload the old save. Manual slot data is intentionally kept
    // so the player can still load a prior save from the save modal.
    localStorage.removeItem('dc_tycoon_active_slot');
    localStorage.removeItem('dc_tycoon_autosave');
    sessionStorage.setItem('dc_skip_load', '1');
    window.location.reload();
  }, []);

  // Title-screen actions.
  const hasSaveOnLoad = !!initialSave;
  const handleContinue = useCallback(() => setShowStartScreen(false), []);
  const handleNewGame = useCallback(() => {
    if (hasSaveOnLoad) {
      // Reset wipes autosave and reloads; flag the reloaded page to skip the
      // title and start the guided tour immediately.
      if (!window.confirm('Start a new game? Your autosave will be cleared (manual saves are kept).')) return;
      sessionStorage.setItem('dc_autostart_tour', '1');
      resetGame();
    } else {
      // No save yet — already a fresh game, just begin and start the tour.
      setShowStartScreen(false);
      setTourActive(true);
    }
  }, [hasSaveOnLoad, resetGame]);
  const dismissTour = useCallback(() => {
    setTourActive(false);
    try { localStorage.setItem('dc_tour_dismissed', '1'); } catch {}
  }, []);

  // Revenue sparkline — sample stats.totalRevenue every 5 ticks, keep last 60 samples
  const sparkSampleRef = useRef(0);
  useEffect(() => {
    if (gameTime - sparkSampleRef.current >= 5) {
      sparkSampleRef.current = gameTime;
      revenueHistoryRef.current = [...revenueHistoryRef.current.slice(-59), stats.totalRevenue];
      setRevenueHistory([...revenueHistoryRef.current]);
    }
  }, [gameTime, stats.totalRevenue]);

  // UPS charging/discharging — runs every 5 game ticks
  useEffect(() => {
    if (isPaused || gameTime % 5 !== 0) return;

    const powerOutageActive = activeEvents.some(e => e.id === 'powerOutage');

    setLocations(prev => {
      let changed = false;
      const next = { ...prev };

      Object.keys(next).forEach(locId => {
        const loc = next[locId];
        if (!loc.unlocked || !loc.ups?.installed) return;

        const ups = loc.ups!;
        const chargeRate = ups.capacityKWh * 0.02; // 2% of capacity per 5 ticks = full charge in ~250 ticks (~4 in-game hours)

        let newCharge = ups.chargeLevelKWh;
        let newCharging = false;
        let newDischarging = false;

        if (powerOutageActive) {
          // If a generator with fuel is installed, it covers the outage — UPS stays on standby
          const generatorCovering = loc.generator?.installed && (loc.generator?.fuelLevelHours ?? 0) > 0;
          if (generatorCovering) {
            // No UPS drain while generator is running
          } else {
            // Discharge: drain at rate proportional to load
            const drainRate = chargeRate * 2;
            newCharge = Math.max(0, ups.chargeLevelKWh - drainRate);
            newDischarging = newCharge > 0;

            // Warn when UPS runs out
            if (ups.chargeLevelKWh > 0 && newCharge === 0) {
              triggerNotification(`UPS at ${loc.name} depleted! Servers going offline.`, 'error');
            }
          }
        } else {
          // Charge when below capacity and power is available
          const hasPower = (loc.availablePowerKW ?? 0) > 0 || loc.usedPower > 0;
          if (hasPower && ups.chargeLevelKWh < ups.capacityKWh) {
            newCharge = Math.min(ups.capacityKWh, ups.chargeLevelKWh + chargeRate);
            newCharging = newCharge < ups.capacityKWh;
          }
        }

        if (newCharge !== ups.chargeLevelKWh || newCharging !== ups.charging || newDischarging !== ups.discharging) {
          changed = true;
          next[locId] = {
            ...loc,
            ups: { ...ups, chargeLevelKWh: newCharge, charging: newCharging, discharging: newDischarging }
          };
        }
      });

      return changed ? next : prev;
    });
  }, [gameTime, isPaused, activeEvents, triggerNotification]);

  // Generator fuel drain — runs every 60 ticks (1 in-game hour)
  // During a power outage: generator starts, drains fuel, covers revenue; when fuel empty, stops.
  useEffect(() => {
    if (isPaused || gameTime % 60 !== 0) return;

    const powerOutageActive = activeEvents.some(e => e.id === 'powerOutage');

    setLocations(prev => {
      let changed = false;
      const next = { ...prev };

      Object.keys(next).forEach(locId => {
        const loc = next[locId];
        if (!loc.unlocked || !loc.generator?.installed) return;

        const gen = loc.generator!;
        if (powerOutageActive) {
          if (gen.fuelLevelHours > 0) {
            const newFuel = Math.max(0, gen.fuelLevelHours - 1);
            const wasRunning = gen.isRunning;
            const nowRunning = newFuel > 0;
            if (!wasRunning) {
              triggerNotification(`Diesel generator at ${loc.name} started — covering the outage.`, 'info');
            }
            if (wasRunning && newFuel === 0) {
              triggerNotification(`Diesel generator at ${loc.name} ran out of fuel!`, 'error');
            }
            changed = true;
            next[locId] = { ...loc, generator: { ...gen, fuelLevelHours: newFuel, isRunning: nowRunning } };
          } else if (gen.isRunning) {
            changed = true;
            next[locId] = { ...loc, generator: { ...gen, isRunning: false } };
          }
        } else if (gen.isRunning) {
          // Outage cleared — stop generator
          changed = true;
          next[locId] = { ...loc, generator: { ...gen, isRunning: false } };
        }
      });

      return changed ? next : prev;
    });
  }, [gameTime, isPaused, activeEvents, triggerNotification]);

  // Contract marketplace refresh — every CONTRACT_REFRESH_DAYS in-game days
  const TICKS_PER_DAY_CONST = 1440;
  const absoluteGameDay = Math.floor(gameTime / TICKS_PER_DAY_CONST);
  useEffect(() => {
    if (absoluteGameDay <= lastContractRefreshDay) return;
    // First ever population (lastContractRefreshDay = 0) happens immediately on day 1.
    // Subsequent refreshes happen every CONTRACT_REFRESH_DAYS days.
    if (lastContractRefreshDay > 0 && (absoluteGameDay - lastContractRefreshDay) < CONTRACT_REFRESH_DAYS) return;

    // Build new marketplace of available contracts
    const unlockedLocationIds = Object.entries(locations)
      .filter(([, loc]) => loc.unlocked)
      .map(([id]) => id);
    if (unlockedLocationIds.length === 0) return;

    // ── Market demand simulation ─────────────────────────────────────────────
    // Compute idle server ratio per location. Locations with many idle servers
    // attract more contract attention; saturated locations (all servers contracted)
    // temporarily dry up. Weight = idleServers / max(1, totalServers).
    // A location with 0 servers gets weight 0 (no market interest yet).
    const locationWeights: Record<string, number> = {};
    for (const locId of unlockedLocationIds) {
      const loc = locations[locId];
      let total = 0, idle = 0;
      Object.values(loc.floors ?? {}).forEach(f =>
        f.racks?.forEach(r =>
          r.servers?.forEach(s => {
            total++;
            if (!s.contractId && s.failureStatus !== 'failed') idle++;
          })
        )
      );
      if (total === 0) {
        locationWeights[locId] = 0; // no servers → market ignores this location
      } else {
        const idleRatio = idle / total;
        // Scale: lots of idle = high weight; fully saturated = 0.05 (trickle)
        locationWeights[locId] = Math.max(0.05, idleRatio);
      }
    }

    // Dynamic marketplace size: base + 1 extra slot per location with >50% idle ratio
    const bonusSlots = unlockedLocationIds.filter(id => (locationWeights[id] ?? 0) > 0.5).length;
    const dynamicSize = Math.min(8, CONTRACT_MARKETPLACE_SIZE + Math.floor(bonusSlots / 2));

    // Filter eligible templates based on current game day
    const eligible = CONTRACT_TEMPLATES.filter(t => t.availableFromDay <= absoluteGameDay);
    if (eligible.length === 0) return;

    // Weighted location picker using idle-server weights
    const pickWeightedLocation = (candidates: string[]): string => {
      const weights = candidates.map(id => locationWeights[id] ?? 0.05);
      const total = weights.reduce((s, w) => s + w, 0);
      if (total === 0) return candidates[Math.floor(Math.random() * candidates.length)];
      let r = Math.random() * total;
      for (let i = 0; i < candidates.length; i++) {
        r -= weights[i];
        if (r <= 0) return candidates[i];
      }
      return candidates[candidates.length - 1];
    };

    // Pick up to dynamicSize unique offers, excluding already-active contracts
    const shuffled = [...eligible].sort(() => Math.random() - 0.5);
    const offers: AvailableContract[] = [];
    for (const template of shuffled) {
      if (offers.length >= dynamicSize) break;
      const validLocIds = (template.locationKeys
        ? unlockedLocationIds.filter(id => template.locationKeys!.includes(id))
        : unlockedLocationIds
      ).filter(locId =>
        !activeContracts.find(c => c.active && c.templateId === template.id && c.locationId === locId)
        && (locationWeights[locId] ?? 0) > 0  // don't offer at empty DCs
      );
      if (validLocIds.length === 0) continue;
      const locId = pickWeightedLocation(validLocIds);
      offers.push({
        template,
        locationId: locId,
        offeredOnDay: absoluteGameDay,
        expiresOnDay: absoluteGameDay + template.expiresAfterDays,
      });
    }

    // Preserve any existing renewal offers — don't wipe them on refresh
    setAvailableContracts(prev => [...prev.filter(o => o.isRenewal), ...offers]);
    setLastContractRefreshDay(absoluteGameDay);
    const highDemandLocs = unlockedLocationIds.filter(id => (locationWeights[id] ?? 0) > 0.7).length;
    if (offers.length > 0) {
      const demandNote = highDemandLocs > 0 ? ` High demand at ${highDemandLocs} location${highDemandLocs > 1 ? 's' : ''} — clients are chasing idle capacity.` : '';
      triggerNotification(`${offers.length} new contract${offers.length > 1 ? 's' : ''} available in the marketplace!${demandNote}`, 'info');
    } else if (unlockedLocationIds.every(id => (locationWeights[id] ?? 0) === 0)) {
      triggerNotification('No contracts available — deploy servers first to attract clients.', 'warning');
    } else if (unlockedLocationIds.every(id => (locationWeights[id] ?? 0) <= 0.1)) {
      triggerNotification('Market is quiet — most of your server capacity is under contract. Free up slots to attract new clients.', 'warning');
    }
  }, [absoluteGameDay, lastContractRefreshDay, locations, activeContracts, triggerNotification]);

  // Contract: expire offers that passed their deadline
  useEffect(() => {
    setAvailableContracts(prev => prev.filter(o => o.expiresOnDay >= absoluteGameDay));
  }, [absoluteGameDay]);

  // Contract: expire completed contracts, free their servers, accumulate daily earned total, generate renewals
  useEffect(() => {
    if (gameTime % TICKS_PER_DAY_CONST !== 0) return;
    const expiring = activeContracts.filter(c => c.active && absoluteGameDay >= c.endDay);
    expiring.forEach(c => {
      if (c.locationAssignments) {
        Object.entries(c.locationAssignments).forEach(([locId, sids]) => freeContractServers(locId, sids));
      } else {
        freeContractServers(c.locationId, c.assignedServerIds);
      }
      triggerNotification(`Contract with ${c.clientName} completed! Total earned: ${formatCurrency(c.totalEarned)}.`, 'success');
    });

    // Generate renewal offers 7 days before expiry
    const renewalCandidates = activeContracts.filter(c =>
      c.active &&
      absoluteGameDay >= c.endDay - 7 &&
      !c.renewalOfferedDay &&
      absoluteGameDay < c.endDay
    );
    if (renewalCandidates.length > 0) {
      setActiveContracts(prev => prev.map(c => {
        if (!renewalCandidates.find(rc => rc.templateId === c.templateId && rc.locationId === c.locationId)) return c;
        return { ...c, renewalOfferedDay: absoluteGameDay };
      }));
      setAvailableContracts(prev => {
        const newOffers = renewalCandidates
          .filter(c => !prev.find(o => o.isRenewal && o.renewsContractTemplateId === c.templateId && o.locationId === c.locationId))
          .map(c => {
            const template = CONTRACT_TEMPLATES.find(t => t.id === c.templateId);
            if (!template) return null;
            const renewalTemplate = {
              ...template,
              revenuePerServerPerDay: Math.round(template.revenuePerServerPerDay * 1.10),
              id: template.id, // same id so duplicate guard still works for active
            };
            return {
              template: renewalTemplate,
              locationId: c.locationId,
              offeredOnDay: absoluteGameDay,
              expiresOnDay: c.endDay + 3, // 3 days after current contract ends
              isRenewal: true,
              renewsContractTemplateId: c.templateId,
            } as import('./types').AvailableContract;
          })
          .filter(Boolean) as import('./types').AvailableContract[];
        if (newOffers.length > 0) {
          newOffers.forEach(o => triggerNotification(
            `Renewal offer from ${o.template.clientName}: +10% rate. Accept before day ${o.expiresOnDay}.`, 'info'
          ));
        }
        return [...prev, ...newOffers];
      });
    }

    setActiveContracts(prev => prev.map(c => {
      if (!c.active) return c;
      if (absoluteGameDay >= c.endDay) return { ...c, active: false };
      // Accrue actual multiplier-adjusted earnings (falls back to gross rate if not yet computed)
      const earnedToday = contractEarningsRef.current[`${c.templateId}|${c.locationId}`] ?? c.bonusRevenuePerDay;
      return { ...c, totalEarned: c.totalEarned + earnedToday };
    }));
  }, [gameTime]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track which (eventId|contractTemplateId|locationId) combos have already triggered a breach this occurrence
  const firedBreachesRef = useRef<Set<string>>(new Set());

  // Contract breach detection — fires once per event occurrence, not every tick
  useEffect(() => {
    const BREACH_EVENTS = new Set(['powerOutage', 'networkOutage', 'fiberCut', 'coolingFailure', 'hardwareRecall']);
    const outageEvents = activeEvents.filter(e => BREACH_EVENTS.has(e.id));

    // Remove breach keys for events that are no longer active (event ended → reset so next occurrence can breach again)
    const activeEventKeys = outageEvents.map(e => e.id + '|' + e.locationId);
    Array.from(firedBreachesRef.current).forEach(key => {
      const eventPart = key.split('||')[0];
      if (!activeEventKeys.includes(eventPart)) firedBreachesRef.current.delete(key);
    });

    if (outageEvents.length === 0) return;

    setActiveContracts(prev => prev.map(c => {
      if (!c.active) return c;
      // Hyperscale contracts breach when any of their assigned locations is affected
      const contractLocations = c.locationAssignments
        ? Object.keys(c.locationAssignments)
        : [c.locationId];
      const matchingEvent = outageEvents.find(e => contractLocations.includes(e.locationId));
      if (!matchingEvent) return c;
      const breachKey = `${matchingEvent.id}|${matchingEvent.locationId}||${c.templateId}`;
      if (firedBreachesRef.current.has(breachKey)) return c; // already fired for this event occurrence
      firedBreachesRef.current.add(breachKey);
      const newBreachCount = c.breachCount + 1;
      setMoney(m => m - c.penaltyPerBreach);
      if (newBreachCount >= 3) {
        // Auto-terminate: too many breaches
        if (c.locationAssignments) {
          Object.entries(c.locationAssignments).forEach(([locId, sids]) => freeContractServers(locId, sids));
        } else {
          freeContractServers(c.locationId, c.assignedServerIds);
        }
        setReputation(prev => Math.max(0, prev - 8));
        triggerNotification(
          `Contract with ${c.clientName} TERMINATED after ${newBreachCount} SLA breaches! Reputation −8.`,
          'error', 12000
        );
        return { ...c, breachCount: newBreachCount, active: false };
      }
      triggerNotification(
        `SLA breach at ${locations[c.locationId]?.name}! ${c.clientName} penalised ${formatCurrency(c.penaltyPerBreach)}. (${newBreachCount}/3 breaches)`,
        'error'
      );
      return { ...c, breachCount: newBreachCount };
    }));
  }, [activeEvents]); // eslint-disable-line react-hooks/exhaustive-deps

  // Bankruptcy detection — 3 consecutive in-game days in the red triggers game over
  useEffect(() => {
    if (gameTime % 1440 !== 0 || gameTime === 0) return;
    if (money < 0) {
      const newCount = bankruptcyDaysNegative + 1;
      setBankruptcyDaysNegative(newCount);
      if (newCount === 1) {
        triggerNotification('Warning: your balance is negative! You have 2 days to recover before bankruptcy.', 'error', 10000);
      } else if (newCount === 2) {
        triggerNotification('Critical: balance still negative! 1 day until bankruptcy proceedings begin.', 'error', 12000);
      } else if (newCount >= 3) {
        setIsPaused(true);
        setShowGameOver(true);
      }
    } else {
      if (bankruptcyDaysNegative > 0) setBankruptcyDaysNegative(0);
    }
  }, [gameTime]); // eslint-disable-line react-hooks/exhaustive-deps

  // Battery trading + renewable integration is handled in useGameLoop (every 60 ticks).
  // Solar/wind reduces grid draw each tick; surplus charges battery or exports to grid.

  // Derive in-game clock from ticks (60 ticks = 1 in-game hour)
  const currentHour = Math.floor((gameTime / 60) % 24);
  const currentMinute = Math.floor(gameTime % 60);
  // In-game calendar: 1 day = 1440 ticks, 1 month = 30 days = 43200 ticks
  const TICKS_PER_DAY = 1440;
  const TICKS_PER_MONTH = TICKS_PER_DAY * 30;
  const gameDay = (Math.floor(gameTime / TICKS_PER_DAY) % 30) + 1;        // 1–30
  const gameMonth = Math.floor(gameTime / TICKS_PER_MONTH) + 1;            // 1, 2, 3 …
  const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const gameMonthName = MONTH_NAMES[(gameMonth - 1) % 12];

  // Resolve current power rate and Peak/Off-peak phase for the selected location's supplier
  const { currentPowerRateKWh, currentTariffPhase } = useMemo(() => {
    const tol = 1e-9;
    const res: { currentPowerRateKWh?: number; currentTariffPhase?: 'Peak' | 'Off-peak' | 'Mid' | 'Fixed' } = {};
    const loc = currentLocationData;
    if (!loc || !loc.powerGridId || !loc.selectedPowerSupplierId) return res;
    const grid = POWER_GRIDS[loc.powerGridId];
    if (!grid) return res;
    const supplier = grid.suppliers.find(s => s.id === loc.selectedPowerSupplierId);
    if (!supplier) return res;

    const schedule = supplier.tariffSchedule as Array<{ startHour: number; endHour: number; rate: number }> | undefined;
    const resolveCurrentRate = (): number => {
      if (!schedule || typeof currentHour !== 'number') return supplier.costPerKWh;
      for (const win of schedule) {
        if (win.startHour <= win.endHour) {
          if (currentHour >= win.startHour && currentHour < win.endHour) return win.rate;
        } else {
          // Overnight wrap-around
          if (currentHour >= win.startHour || currentHour < win.endHour) return win.rate;
        }
      }
      return supplier.costPerKWh;
    };

    const cr = resolveCurrentRate();
    let phase: 'Peak' | 'Off-peak' | 'Mid' | 'Fixed' = 'Fixed';
    if (schedule && schedule.length > 0) {
      const rates = schedule.map(w => w.rate);
      const minR = Math.min(...rates);
      const maxR = Math.max(...rates);
      if (Math.abs(cr - minR) < tol) phase = 'Off-peak';
      else if (Math.abs(cr - maxR) < tol) phase = 'Peak';
      else phase = 'Mid';
    }

    return { currentPowerRateKWh: cr, currentTariffPhase: phase };
  }, [currentLocationData, currentHour]);

  // Time Scrubber handler: set hour, preserve minutes (ticks within hour)
  const handleScrubHour = useCallback((newHour: number) => {
    setGameTime(prev => {
      const minute = Math.floor(prev % 60);
      return ((newHour % 24) * 60) + minute;
    });
  }, [setGameTime]);

  // Function to unlock a location
  const unlockLocation = useCallback((locationId: string) => {
    if (!locations[locationId] || locations[locationId].unlocked) return;
    
    const unlockCost = locations[locationId].unlockCost || 0;
    if (money < unlockCost) return;
    
    setMoney(prev => prev - unlockCost);
    
    setLocations(prev => {
      const newLocations = { ...prev };
      newLocations[locationId] = {
        ...newLocations[locationId],
        unlocked: true
      };
      return newLocations;
    });
    
    triggerNotification(`Unlocked ${locations[locationId].name}!`, 'success');
    setCurrentLocation(locationId);
  }, [locations, money, triggerNotification]);
  
  // Auto-select Floor 1 when location changes (or first available floor)
  useEffect(() => {
    const loc = locations[currentLocation];
    if (!loc || !loc.floors) return;
    const floorKeys = Object.keys(loc.floors);
    if (floorKeys.length === 0) return;

    // If the current floor exists in this location, keep user's selection
    if (floorKeys.includes(currentFloor)) return;

    const preferred = floorKeys.includes('floor1')
      ? 'floor1'
      : floorKeys
          .slice()
          .sort((a, b) => {
            const na = parseInt(a.replace(/\D/g, '')) || Number.MAX_SAFE_INTEGER;
            const nb = parseInt(b.replace(/\D/g, '')) || Number.MAX_SAFE_INTEGER;
            return na - nb || a.localeCompare(b);
          })[0];

    if (currentFloor !== preferred) setCurrentFloor(preferred);
  }, [currentLocation, locations, currentFloor]);

  // Function to buy an upgrade
  const buyUpgrade = useCallback((upgradeId: string) => {
    const upgrade = UPGRADE_TYPES.find((u: UpgradeType) => u.id === upgradeId);
    if (!upgrade) return;
    
    const currentLevel = upgrades[upgradeId] || 0;
    const cost = upgrade.baseCost * Math.pow(2, currentLevel);
    
    if (money < cost) return;
    
    setMoney(prev => prev - cost);
    
    setUpgrades(prev => ({
      ...prev,
      [upgradeId]: (prev[upgradeId] || 0) + 1
    }));
    
    triggerNotification(`Upgraded ${upgrade.name} to level ${currentLevel + 1}!`, 'success');
  }, [upgrades, money, triggerNotification]);
  
  // The upgradeFloorQuality and getFloorCoolingEfficiency functions are now imported from useFloorManagement hook

  const openDesignerForEdit = useCallback((floorId: string) => {
    setDesignerEditFloorId(floorId);
    setDesignerInitialTemplate(null);
    setDesignerAssignRoomId(null);
    setShowFloorDesigner(true);
  }, []);

  return (
    <React.Fragment>
      {showStartScreen && (
        <StartScreen
          hasSave={hasSaveOnLoad}
          onContinue={handleContinue}
          onNewGame={handleNewGame}
          saveSummary={hasSaveOnLoad
            ? `Day ${Math.floor((initialSave?.gameTime ?? 0) / 1440) + 1} · ${formatCurrency(initialSave?.money ?? 0)}`
            : undefined}
        />
      )}
      {(() => {
        const sp = getSetupProgress(currentLocationData);
        return (
          <>
            <GuidedTour
              active={tourActive && !showStartScreen && !sp.complete && !overlayOpen}
              targetId="setup-guide"
              onDismiss={dismissTour}
              done={sp.done}
              totalSteps={sp.total}
              nextLabel={sp.nextLabel}
            />
            {/* Re-open handle: shown when the tour is hidden but setup isn't done yet. */}
            {!tourActive && !showStartScreen && !sp.complete && sp.total > 0 && !overlayOpen && (
              <button
                onClick={() => setTourActive(true)}
                className="fixed bottom-4 left-4 z-40 flex items-center gap-2 px-3 py-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold shadow-lg shadow-blue-900/40"
                title="Reopen the setup guide"
              >
                <Sparkles className="w-4 h-4" /> Setup help ({sp.done}/{sp.total})
              </button>
            )}
          </>
        );
      })()}

      <div className="min-h-screen bg-gray-900 text-white flex flex-col items-center">
        <div className="container mx-auto p-4">

          {/* STATS BAR */}
          <StatsBar
            stats={stats}
            money={money}
            formatCurrency={formatCurrency}
            operationalCosts={operationalCosts}
            currentHour={currentHour}
            currentMinute={currentMinute}
            onScrubHour={handleScrubHour}
            currentPowerRateKWh={currentPowerRateKWh}
            currentTariffPhase={currentTariffPhase}
            showPowerWarning={showPowerWarning}
            powerWarningMessage={powerWarningMessage}
            pue={pue}
            gameDay={gameDay}
            gameMonthName={gameMonthName}
            gameMonth={gameMonth}
            revenueHistory={revenueHistory}
            transitCostPerDay={transitCostPerDay}
            bandwidthPenaltyPerDay={bandwidthPenaltyPerDay}
            bandwidthBreakdown={bandwidthBreakdown}
            ixpRevenueBonusPerDay={ixpRevenueBonusPerDay}
            revenueBreakdown={revenueBreakdown}
            coolingStats={coolingStats}
            transitStats={transitStats}
            eventRevenueModifier={eventRevenueModifier}
            netPerDay={dailyFinancials.net}
            activeEvents={activeEvents}
            reputation={reputation}
          />

          {/* LOCATION MAP */}
          <LocationMap
            locations={locations}
            currentLocation={currentLocation}
            setCurrentLocation={setCurrentLocation}
            unlockLocation={unlockLocation}
            money={money}
            formatCurrency={formatCurrency}
            backboneLinks={backboneLinks}
            backboneLinkStats={backboneLinkStats}
          />

        {/* MAIN CONTENT AREA (DATACENTER VIEW & MANAGEMENT PANEL) */}
        <div className="mx-auto w-full bg-gray-900 text-white p-4 min-h-screen flex flex-col">
        {currentLocationData !== null ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Datacenter View Component */}
            <DatacenterView
              currentLocationData={currentLocationData}
              currentFloorData={currentFloorData}
              selectedRack={selectedRack}
              setSelectedRack={setSelectedRack}
              selectedRackObj={selectedRackObj}
              selectedRackType={selectedRackType}
              selectedRackUsedPower={selectedRackUsedPower}
              rackTypes={RACK_TYPES}
              serverTypes={SERVER_TYPES}
              torSwitchTypes={TOR_SWITCH_TYPES}
              playerLevel={playerLevel}
              money={money}
              currentLocation={currentLocation}
              buyRack={buyRack}
              installServer={installServer}
              fillRack={fillRack}
              installTORSwitch={installTORSwitch}
              installMpoPatchPanel={installMpoPatchPanel}
              upgradeMpoPatchPanel={upgradeMpoPatchPanel}
              installRedundantTorSwitch={installRedundantTorSwitch}
              installManagementSwitch={installManagementSwitch}
              removeServer={removeServer}
              removeRack={removeRack}
              repairServer={repairServer}
              gameTime={gameTime}
              notifications={notifications}
              removeNotification={removeNotification}
              setShowUpgrades={setShowUpgrades}
              locations={locations}
              upgradeFloorQuality={upgradeFloorQuality}
              getFloorCoolingEfficiency={getFloorCoolingEfficiency}
              setShowPowerManagementModal={setShowPowerManagementModal}
              setShowGoals={setShowGoals}
              setShowChillerManagementModal={setShowChillerManagementModal}
              currentFloor={currentFloor}
              setCurrentFloor={setCurrentFloor}
              setShowFloorDesigner={setShowFloorDesigner}
              openDesignerForEdit={openDesignerForEdit}
              formatCurrency={formatCurrency}
              gameSpeed={gameSpeed}
              setGameSpeed={setGameSpeed}
              isPaused={isPaused}
              setIsPaused={setIsPaused}
              reputation={reputation}
              powerEfficiencyUpgrade={upgrades.powerEfficiency || 0}
              serverPerformanceUpgrade={upgrades.serverPerformance || 0}
              setShowSiteLayoutEditor={setShowSiteLayoutEditor}
              setShowStaffModal={setShowStaffModal}
              setShowNetworkModal={setShowNetworkModal}
              setShowMmrModal={setShowMmrModal}
              setShowFinancials={setShowFinancials}
              setShowFloorModal={setShowFloorModal}
              setShowBackboneModal={setShowBackboneModal}
              setShowContractsModal={setShowContractsModal}
              setShowGreenEnergyModal={setShowGreenEnergyModal}
              setShowLoansModal={setShowLoansModal}
              setShowFleetOverview={setShowFleetOverview}
              activeContractCount={activeContracts.filter(c => c.active).length}
              activeLoanCount={loans.filter(l => !l.paidOff).length}
              activeContracts={activeContracts}
              pendingDeliveries={pendingDeliveries}
              gameDay={absoluteGameDay}
              buildMmr={buildMmr}
              installCoreSwitch={installCoreSwitch}
              staffCount={staff.length}
              onOpenSaveModal={() => setShowSaveModal(true)}
              onResetGame={resetGame}
              onOpenEventLog={() => { setShowEventLog(true); setEventLogUnread(0); }}
              eventLogUnread={eventLogUnread}
              manageUnlockPulse={manageUnlockPulse}
              onManageOpened={() => setManageUnlockPulse(false)}
            />
            
            {/* 3D View Test Toggle Button - Easy to remove if not needed */}
            <Test3DViewToggle locations={locations} currentLocation={currentLocation} />
          </div>
        ) : (
          <div className="text-center p-8 bg-gray-800 rounded-lg">
            <h2 className="text-xl font-bold mb-2">No Location Selected</h2>
            <p className="text-gray-400">Please select a datacenter location from the manager above to view its details.</p>
          </div>
        )}
        </div>

        {/* STAFF MODAL */}
        <StaffModal
          isOpen={showStaffModal}
          onClose={() => setShowStaffModal(false)}
          staff={staff}
          money={money}
          hireStaff={hireStaff}
          fireStaff={fireStaff}
          getDailyStaffCost={getDailyStaffCost}
          formatCurrency={formatCurrency}
        />

        {/* NETWORK MANAGEMENT MODAL */}
        <NetworkManagementModal
          isOpen={showNetworkModal}
          onClose={() => setShowNetworkModal(false)}
          currentLocation={currentLocation}
          locations={locations}
          installHdaSwitch={installHdaSwitch}
          installCoreSwitch={installCoreSwitch}
          formatCurrency={formatCurrency}
          money={money}
          onGoToConnectivity={() => { setShowNetworkModal(false); setShowMmrModal(true); }}
        />

        {/* MMR / CONNECTIVITY MODAL */}
        <MmrModal
          isOpen={showMmrModal}
          onClose={() => setShowMmrModal(false)}
          currentLocation={currentLocation}
          locations={locations}
          asnPurchased={asnPurchased}
          piIpPurchased={piIpPurchased}
          money={money}
          buildMmr={buildMmr}
          purchaseAsn={purchaseAsn}
          purchasePiIpSpace={purchasePiIpSpace}
          orderTransit={orderTransit}
          cancelTransit={cancelTransit}
          connectIxp={connectIxp}
          disconnectIxp={disconnectIxp}
          subscribeNawas={subscribeNawas}
          cancelNawas={cancelNawas}
          formatCurrency={formatCurrency}
          onOpenNetworkModal={() => { setShowMmrModal(false); setShowNetworkModal(true); }}
        />

        {/* FLOOR QUALITY MODAL */}
        {showFloorModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 rounded-xl border border-gray-700 shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700 shrink-0">
                <h2 className="text-lg font-bold">Floor Quality</h2>
                <button onClick={() => setShowFloorModal(false)} className="text-gray-400 hover:text-white">✕</button>
              </div>
              <div className="flex-1 overflow-auto p-5">
                <FloorManagement
                  currentLocation={currentLocation}
                  currentFloor={currentFloor}
                  locations={locations}
                  upgradeFloorQuality={upgradeFloorQuality}
                  getFloorCoolingEfficiency={getFloorCoolingEfficiency}
                  formatCurrency={formatCurrency}
                />
              </div>
            </div>
          </div>
        )}

        {/* FINANCIALS MODAL */}
        <FinancialsModal
          isOpen={showFinancials}
          onClose={() => setShowFinancials(false)}
          locations={locations}
          formatCurrency={formatCurrency}
          currentHour={currentHour}
          reputation={reputation}
          unlockLevel={playerLevel}
          powerEfficiencyUpgrade={upgrades.powerEfficiency || 0}
          coolingEfficiencyUpgrade={upgrades.coolingEfficiency || 0}
          dailySnapshots={dailySnapshots}
        />

        {/* EVENT LOG MODAL */}
        <EventLogModal
          isOpen={showEventLog}
          onClose={() => setShowEventLog(false)}
          entries={eventLog}
        />

        {/* SAVE / LOAD MODAL */}
        {showSaveModal && (
          <SaveLoadModal
            onClose={() => setShowSaveModal(false)}
            onSave={(slot, name) => saveToSlot(slot, name)}
            onLoad={(slot) => { loadFromSlot(slot); setShowSaveModal(false); }}
            onDelete={deleteSlot}
            money={money}
            gameTime={gameTime}
            reputation={reputation}
          />
        )}

        {/* UPGRADES MODAL */}
        <UpgradesModal
          showUpgrades={showUpgrades}
          setShowUpgrades={setShowUpgrades}
          upgrades={upgrades}
          money={money}
          buyUpgrade={buyUpgrade}
          formatCurrency={formatCurrency}
        />
        
        {/* GOALS MODAL */}
        <GoalsModal
          showGoals={showGoals}
          setShowGoals={setShowGoals}
          goals={goals}
          goalProgress={goalProgress}
          completedGoals={completedGoals}
          claimedGoals={claimedGoals}
          claimGoal={claimGoal}
          formatCurrency={formatCurrency}
        />
        
        {/* POWER MANAGEMENT MODAL */}
        <PowerManagementModal
          showPowerManagementModal={showPowerManagementModal}
          setShowPowerManagementModal={setShowPowerManagementModal}
          currentLocationData={currentLocationData}
          currentLocation={currentLocation}
          money={money}
          buyPowerUpgrade={buyPowerUpgrade}
          selectPowerSupplier={selectPowerSupplier}
          buyGenerator={buyGenerator}
          refuelGenerator={refuelGenerator}
          powerGrids={POWER_GRIDS}
          triggerNotification={triggerNotification}
          setLocations={setLocations}
          transformerLevels={TRANSFORMER_LEVELS}
          powerhouseLevels={POWERHOUSE_LEVELS}
          upsLevels={UPS_LEVELS}
          formatCurrency={formatCurrency}
          currentHour={currentHour}
          setMoney={setMoney}
          onGoToCooling={() => { setShowPowerManagementModal(false); setShowChillerManagementModal(true); }}
        />

        {/* CHILLER MANAGEMENT MODAL */}
        <ChillerManagementModal
          isOpen={showChillerManagementModal}
          onClose={() => setShowChillerManagementModal(false)}
          currentLocationData={currentLocationData}
          buyChiller={buyChiller}
          upgradeChiller={upgradeChiller}
          installHeatRejection={installHeatRejection}
          upgradeHeatRejection={upgradeHeatRejection}
          installCrah={installCrah}
          upgradeCrah={upgradeCrah}
          formatCurrency={formatCurrency}
          currentLocation={currentLocation}
          onGoToServers={() => { setShowChillerManagementModal(false); setSelectedRack(null); }}
        />

        {/* FLOOR DESIGNER MODAL */}
        {showFloorDesigner && (
          <FloorDesigner
            currentLocation={currentLocation}
            locations={locations}
            setLocations={setLocations}
            setShowFloorDesigner={() => {
              setDesignerEditFloorId(null);
              setDesignerInitialTemplate(null);
              setDesignerAssignRoomId(null);
              setShowFloorDesigner(false);
            }}
            formatCurrency={formatCurrency}
            money={money}
            setMoney={setMoney}
            triggerNotification={triggerNotification}
            editMode={!!designerEditFloorId}
            editFloorId={designerEditFloorId || undefined}
            initialTemplate={designerInitialTemplate || undefined}
            assignRoomId={designerAssignRoomId || undefined}
          />
        )}
        
        {/* BACKBONE / DARK FIBER MODAL */}
        {showBackboneModal && (
          <BackboneModal
            onClose={() => setShowBackboneModal(false)}
            locations={locations}
            backboneLinks={backboneLinks}
            money={money}
            onInstall={installBackboneLink}
            onUpgrade={upgradeBackboneLink}
            onRemove={removeBackboneLink}
            backboneCostPerDay={backboneCostPerDay}
            formatCurrency={formatCurrency}
          />
        )}

        {/* CONTRACTS MODAL */}
        {showContractsModal && (
          <ContractsModal
            onClose={() => setShowContractsModal(false)}
            availableContracts={availableContracts}
            activeContracts={activeContracts}
            locations={locations}
            greenCertifiedLocations={greenCertifiedLocations}
            gameDay={absoluteGameDay}
            reputation={reputation}
            onAccept={acceptContract}
            onTerminate={terminateContract}
            formatCurrency={formatCurrency}
          />
        )}

        {showLoansModal && (
          <LoansModal
            onClose={() => setShowLoansModal(false)}
            money={money}
            loans={loans}
            gameDay={absoluteGameDay}
            onTakeLoan={takeLoan}
            onRepayLoan={repayLoan}
            formatCurrency={formatCurrency}
          />
        )}

        {/* GREEN ENERGY MODAL */}
        {showGreenEnergyModal && (
          <GreenEnergyModal
            onClose={() => setShowGreenEnergyModal(false)}
            currentLocation={currentLocation}
            locations={locations}
            money={money}
            currentHour={currentHour}
            onInstallRenewable={installRenewable}
            onInstallBattery={installBattery}
            onToggleBatteryTrading={toggleBatteryTrading}
            renewablePowerKWByLocation={renewablePowerKWByLocation}
            greenCertifiedLocations={greenCertifiedLocations}
            formatCurrency={formatCurrency}
          />
        )}

        {/* FLEET OVERVIEW MODAL */}
        {showFleetOverview && (
          <FleetOverviewModal
            onClose={() => setShowFleetOverview(false)}
            locations={locations}
            formatCurrency={formatCurrency}
          />
        )}

        {/* GAME OVER SCREEN */}
        {showGameOver && (
          <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4">
            <div className="bg-gray-900 border-2 border-red-700 rounded-2xl max-w-md w-full p-8 text-center shadow-2xl">
              <div className="text-6xl mb-4">💸</div>
              <h2 className="text-2xl font-bold text-red-400 mb-2">Bankruptcy</h2>
              <p className="text-gray-300 mb-1">Your balance has been negative for 3 consecutive days.</p>
              <p className="text-gray-400 text-sm mb-6">Creditors have seized operations. Game over.</p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => { setShowGameOver(false); setShowSaveModal(true); }}
                  className="px-5 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium"
                >
                  Load Save
                </button>
                <button
                  onClick={() => { resetGame(); setShowGameOver(false); }}
                  className="px-5 py-2 rounded-lg bg-red-700 hover:bg-red-600 text-white text-sm font-medium"
                >
                  Start Over
                </button>
              </div>
            </div>
          </div>
        )}

        {/* SITE LAYOUT EDITOR MODAL */}
        {showSiteLayoutEditor && (
          <SiteLayoutEditor
            currentLocation={currentLocation}
            locations={locations}
            setLocations={setLocations}
            onClose={() => setShowSiteLayoutEditor(false)}
            triggerNotification={triggerNotification}
            onOpenFloor={(floorId) => {
              setShowSiteLayoutEditor(false);
              setDesignerEditFloorId(floorId);
              setDesignerInitialTemplate(null);
              setDesignerAssignRoomId(null);
              setShowFloorDesigner(true);
            }}
            onOpenRoom={(roomId, template) => {
              setShowSiteLayoutEditor(false);
              setDesignerEditFloorId(null);
              setDesignerInitialTemplate(template);
              setDesignerAssignRoomId(roomId);
              setShowFloorDesigner(true);
            }}
          />
        )}
      </div>
    </div>
    </React.Fragment>
  );
}

export default App;
